import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, getChannelInfo } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Globe,
  Settings2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Loader2,
  ExternalLink,
  ShieldCheck,
  Mail,
} from "lucide-react";

// Default WooCommerce order statuses (fallback)
const defaultWcStatuses = [
  { value: "pending", label: "Ödeme Bekliyor" },
  { value: "processing", label: "İşleniyor" },
  { value: "on-hold", label: "Beklemede" },
  { value: "completed", label: "Tamamlanmış" },
];

// VAT rates
const vatRates = [
  { value: "0", label: "%0" },
  { value: "1", label: "%1" },
  { value: "8", label: "%8" },
  { value: "10", label: "%10" },
  { value: "18", label: "%18" },
  { value: "20", label: "%20" },
];
const n11VatRates = [
  { value: "0", label: "%0" },
  { value: "1", label: "%1" },
  { value: "10", label: "%10" },
  { value: "20", label: "%20" },
];

const trendyolInvoiceStatuses = [
  "Created",
  "Picking",
  "Invoiced",
  "Shipped",
  "Delivered",
  "AtCollectionPoint",
  "UnDelivered",
];

const trendyolStatusLabels = {
  Created: "Oluşturuldu",
  Picking: "Hazırlanıyor",
  Invoiced: "Faturalandı",
  Shipped: "Kargoya Verildi",
  Delivered: "Teslim Edildi",
  AtCollectionPoint: "Toplama Noktasında",
  UnDelivered: "Teslim Edilemedi",
};

const hepsiburadaInvoiceStatuses = [
  { value: "NewOrder", label: "Yeni Sipariş" },
  { value: "Approved", label: "Onaylandı" },
  { value: "Packed", label: "Hazırlandı" },
  { value: "Shipped", label: "Kargoya Verildi" },
  { value: "Delivered", label: "Teslim Edildi" },
];

const hepsiburadaCargoCompanies = [
  "HepsiJet",
  "Yurtiçi Kargo",
  "Aras Kargo",
  "MNG Kargo",
  "Sürat Kargo",
  "PTT Kargo",
  "UPS",
  "DHL",
];

const amazonHelp = {
  store_name: "Panelde görünecek mağaza adıdır.",
  application_id: "Amazon Seller Central'da uygulamanız için tanımlı Application ID.",
  lwa_client_id: "Login With Amazon (LWA) Client ID.",
  lwa_client_secret: "LWA Client Secret. Güvenlik için kaydedildikten sonra maskelenebilir.",
  redirect_uri: "Amazon uygulamanızda tanımladığınız redirect/callback URL.",
  region: "Seller Central bölgesi. Türkiye/EU mağazaları için TR önerilir.",
  pull_all_orders: "Amazon siparişleri otomatik olarak panele alınır.",
  invoice_statuses: "Seçilen sipariş durumlarına göre fatura otomasyonu çalışır.",
  selected_warehouse_id: "Bu entegrasyonun ürün eşleştirme ekranında kullanılacak Paraşüt deposunu belirler.",
};

const hepsiburadaHelp = {
  store_name: "Panelde görünecek mağaza adıdır.",
  vat_rate: "Fatura satırlarında kullanılacak varsayılan KDV oranıdır. Ürün eşleşmesinde ürün KDV'si yoksa bu oran kullanılır.",
  prices_include_vat: "Hepsiburada sipariş tutarları KDV dahil geldiği için açık kalması önerilir.",
  tax_exemption_enabled: "Vergi muafiyeti kapsamındaki ürünler için özel fatura kuralları kullanılır.",
  maturity_difference_days: "Vadeli satışlarda fatura tarihine eklenecek gün sayısı.",
  create_waybill_invoice: "Faturaları irsaliyeli olarak oluşturur.",
  mark_as_paid: "Oluşan faturalar tahsil edilmiş olarak işaretlenir.",
  payment_account_id: "Tahsilat kaydı için kullanılacak Paraşüt kasa/banka hesabı.",
  international_as_export: "Yurtdışı siparişlerde e-ihracat fatura tipi kullanılır.",
  auto_create_e_document: "e-Fatura/e-Arşiv dokümanını otomatik üretir.",
  e_invoice_type: "e-Belge oluşturulurken kullanılacak belge tipi.",
  cargo_company: "Siparişlerde varsayılan kargo firması.",
  disable_cargo_for_order: "Açık olduğunda kargo satırı/taşıma alanı kullanılmaz.",
  auto_email_e_invoice: "Elektronik faturayı müşteri e-posta adresine otomatik gönderir.",
  invoice_note: "Faturanın not alanına eklenecek sabit metin.",
  daily_order_summary: "Her gün sipariş özeti e-postası gönderir.",
  pull_all_orders: "Hepsiburada siparişleri otomatik olarak panele alınır.",
  invoice_statuses: "Seçilen sipariş durumları faturalandırmaya dahil edilir.",
  add_order_note_to_invoice: "Sipariş notunu fatura notuna ekler.",
  selected_warehouse_id: "Bu entegrasyonun ürün eşleştirme ekranında kullanılacak Paraşüt deposunu belirler.",
};

const trendyolHelp = {
  payment_account_id: "Paraşüt'te tahsilat işlemi için kullanılacak kasa/banka hesabını seçin.",
  invoice_tag_id: "Kesilen faturalara otomatik etiket atamak için Paraşüt etiketini seçin.",
  customer_category_id: "Yeni müşteriler Paraşüt'e aktarılırken bu kategoriyle açılır.",
  invoice_category_id: "Satış faturalarının Paraşüt'te hangi kategori altında oluşacağını belirler.",
  cargo_company: "Siparişlerde kullanılacak kargo firmasını seçin. Liste Trendyol ve tanımlı sağlayıcılardan derlenir.",
  tax_exemption_code: "Muaf ürünleriniz varsa uygun vergi muafiyet kodunu seçin.",
  tax_exemption_reason: "Seçilen muafiyet kodunun fatura üzerinde görünecek açıklamasıdır.",
  invoice_statuses: "Seçtiğiniz Trendyol durumlarına gelen siparişler fatura otomasyonuna dahil edilir.",
  selected_warehouse_id: "Bu entegrasyonun ürün eşleştirme ekranında kullanılacak Paraşüt deposunu belirler.",
  tax_exemption_enabled: "Vergi muafiyetine tabi ürünleriniz varsa muafiyet sebebi seçimi açılır.",
};

const n11InvoiceStatuses = [
  { value: "Created", label: "Oluşturuldu" },
  { value: "Approved", label: "Onaylandı" },
  { value: "Shipped", label: "Kargoya Verildi" },
  { value: "Delivered", label: "Teslim Edildi" },
];

const idefixInvoiceStatuses = [
  { value: "shipment_ready", label: "Sipariş Hazır" },
  { value: "shipment_picking", label: "Hazırlanıyor" },
  { value: "shipment_invoiced", label: "Faturalandı" },
  { value: "shipment_in_cargo", label: "Kargoda" },
  { value: "shipment_delivered", label: "Teslim Edildi" },
];

const idefixHelp = {
  vendor_id: "Idefix Hesabım > Entegrasyon Bilgileri ekranındaki Satıcı ID.",
  api_key: "Idefix tarafından gönderilen Token alanı.",
  api_secret: "Idefix tarafından gönderilen Secret alanı.",
  pull_all_orders: "Idefix siparişleri otomatik olarak panele alınır.",
  invoice_statuses: "Seçilen shipment durumlarına gelen siparişler fatura otomasyonuna dahil edilir.",
  invoice_category_id: "Satış faturalarının Paraşüt'te hangi kategori altında oluşacağını belirler.",
  selected_warehouse_id: "Bu entegrasyonun ürün eşleştirme ekranında kullanılacak Paraşüt deposunu belirler.",
};

const VAT_EXEMPTION_OPTIONS = [
  { code: "201", reason: "17/1 Kültür ve Eğitim Amacı Taşıyan İşlemler" },
  { code: "202", reason: "17/2-a Sağlık, Çevre Ve Sosyal Yardım Amaçlı İşlemler" },
  { code: "204", reason: "17/2-c Yabancı Diplomatik Organ Ve Hayır Kurumlarının Yapacakları Bağışlarla İlgili Mal Ve Hizmet Alışları" },
  { code: "205", reason: "17/2-d Taşınmaz Kültür Varlıklarına İlişkin Teslimler ve Mimarlık Hizmetleri" },
  { code: "206", reason: "17/2-e Mesleki Kuruluşların İşlemleri" },
  { code: "207", reason: "17/3 Askeri Fabrika, Tersane ve Atölyelerin İşlemleri" },
  { code: "208", reason: "17/4-c Birleşme, Devir, Dönüşüm ve Bölünme İşlemleri" },
  { code: "209", reason: "17/4-e Banka ve Sigorta Muameleleri Vergisi Kapsamına Giren İşlemler" },
  { code: "211", reason: "17/4-h Zirai Amaçlı Su Teslimleri İle Köy Tüzel Kişiliklerince Yapılan İçme Suyu teslimleri" },
  { code: "212", reason: "17/4-ı Serbest Bölgelerde Verilen Hizmetler" },
  { code: "213", reason: "17/4-j Boru Hattı İle Yapılan Petrol Ve Gaz Taşımacılığı" },
  { code: "214", reason: "17/4-k Organize Sanayi Bölgelerindeki Arsa ve İşyeri Teslimleri İle Konut Yapı Kooperatiflerinin Üyelerine Konut Teslimleri" },
  { code: "215", reason: "17/4-l Varlık Yönetim Şirketlerinin İşlemleri" },
  { code: "216", reason: "17/4-m Tasarruf Mevduatı Sigorta Fonunun İşlemleri" },
  { code: "217", reason: "17/4-n Basın-Yayın ve Enformasyon Genel Müdürlüğüne Verilen Haber Hizmetleri" },
  { code: "218", reason: "KDV 17/4-o md. Gümrük Antrepoları, Geçici Depolama Yerleri ile Gümrüklü Sahalarda Vergisiz Satış Yapılan İşyeri, Depo ve Ardiye Gibi Bağımsız Birimlerin Kiralanması" },
  { code: "219", reason: "Hazine , Toplu Konut İdaresi Başkanlığı, Belediyeler, il özel idareleri ve yatırım izleme ve koordinasyon başkanlıklarının İşlemleri" },
  { code: "220", reason: "17/4-r İki Tam Yıl Süreyle Sahip Olunan Taşınmaz ve İştirak Hisseleri ile 15/7/2023 tarihinden önce kurumların aktifinde kayıtlı Taşınmaz satışı" },
  { code: "221", reason: "Geçici 15 Konut Yapı Kooperatifleri, Belediyeler ve Sosyal Güvenlik Kuruluşlarına Verilen İnşaat Taahhüt Hizmeti" },
  { code: "223", reason: "Geçici 20/1 Teknoloji Geliştirme Bölgelerinde Yapılan İşlemler" },
  { code: "225", reason: "Geçici 23 Milli Eğitim Bakanlığına Yapılan Bilgisayar Bağışları İle İlgili Teslimler" },
  { code: "226", reason: "17/2-b Özel Okulları, Üniversite ve Yüksekokullar Tarafından Verilen Bedelsiz Eğitim Ve Öğretim Hizmetleri" },
  { code: "227", reason: "17/2-b Kanunların Gösterdiği Gerek Üzerine Bedelsiz Olarak Yapılan Teslim ve Hizmetler" },
  { code: "228", reason: "17/2-b Kanunun (17/1) Maddesinde Sayılan Kurum ve Kuruluşlara Bedelsiz Olarak Yapılan Teslimler" },
  { code: "229", reason: "17/2-b Gıda Bankacılığı Faaliyetinde Bulunan Dernek ve Vakıflara Bağışlanan Gıda, Temizlik, Giyecek ve Yakacak Maddeleri" },
  { code: "230", reason: "17/4-g Külçe Altın, Külçe Gümüş Ve Kiymetli Taşlarin Teslimi" },
  { code: "231", reason: "17/4-g Metal Plastik, Lastik, Kauçuk, Kağit, Cam Hurda Ve Atıkların Teslimi" },
  { code: "232", reason: "17/4-g Döviz, Para, Damga Pulu, Değerli Kağıtlar, Hisse Senedi ve Tahvil Teslimleri" },
  { code: "234", reason: "17/4-ş Konut Finansmanı Amacıyla Teminat Gösterilen ve İpotek Konulan Konutların Teslimi" },
  { code: "235", reason: "16/1-c Transit ve Gümrük Antrepo Rejimleri İle Geçici Depolama ve Serbest Bölge Hükümlerinin Uygulandığiı Malların Teslimi" },
  { code: "236", reason: "19/2 Usulüne Göre Yürürlüğe Girmiş Uluslararası Anlaşmalar Kapsamındaki İstisnalar (İade Hakkı Tanınmayan)" },
  { code: "237", reason: "17/4-t 5300 Sayılı Kanuna Göre Düzenlenen Ürün Senetlerinin İhtisas/Ticaret Borsaları Aracılığıyla İlk Teslimlerinden Sonraki Teslim" },
  { code: "238", reason: "17/4-u Varlıkların Varlık Kiralama Şirketlerine Devri İle Bu Varlıkların Varlık Kiralama Şirketlerince Kiralanması ve Devralınan Kuruma Devri" },
  { code: "239", reason: "17/4-y Taşınmazların Finansal Kiralama Şirketlerine Devri, Finansal Kiralama Şirketi Tarafından Devredene Kiralanması ve Devri" },
  { code: "240", reason: "17/4-z Patentli Veya Faydalı Model Belgeli Buluşa İlişkin Gayri Maddi Hakların Kiralanması, Devri ve Satışı" },
  { code: "241", reason: "TürkAkım Gaz Boru Hattı Projesine İlişkin Anlaşmanın (9/b) Maddesinde Yer Alan Hizmetler" },
  { code: "242", reason: "KDV 17/4-ö md. Gümrük Antrepoları, Geçici Depolama Yerleri ile Gümrüklü Sahalarda, İthalat ve İhracat İşlemlerine konu mallar ile transit rejim kapsamında işlem gören mallar için verilen ardiye, depolama ve terminal hizmetleri" },
  { code: "250", reason: "Diğerleri" },
  { code: "301", reason: "11/1-a Mal İhracatı" },
  { code: "302", reason: "11/1-a Hizmet İhracatı" },
  { code: "303", reason: "11/1-a Roaming Hizmetleri" },
  { code: "304", reason: "13/a Deniz Hava ve Demiryolu Taşıma Araçlarının Teslimi İle İnşa, Tadil, Bakım ve Onarımları" },
  { code: "305", reason: "13/b Deniz ve Hava Taşıma Araçları İçin Liman Ve Hava Meydanlarında Yapılan Hizmetler" },
  { code: "306", reason: "13/c Petrol Aramaları ve Petrol Boru Hatlarının İnşa ve Modernizasyonuna İlişkin Yapılan Teslim ve Hizmetler" },
  { code: "307", reason: "13/c Maden Arama, Altın, Gümüş ve Platin Madenleri İçin İşletme, Zenginleştirme Ve Rafinaj Faaliyetlerine İlişkin Teslim Ve Hizmetler[KDVGUT-(II/8-4)]" },
  { code: "308", reason: "13/d Teşvikli Yatırım Mallarının Teslimi" },
  { code: "309", reason: "13/e Liman Ve Hava Meydanlarının İnşası, Yenilenmesi Ve Genişletilmesi" },
  { code: "310", reason: "13/f Ulusal Güvenlik Amaçlı Teslim ve Hizmetler" },
  { code: "311", reason: "14/1 Uluslararası Taşımacılık" },
  { code: "312", reason: "15/a Diplomatik Organ Ve Misyonlara Yapılan Teslim ve Hizmetler" },
  { code: "313", reason: "15/b Uluslararası Kuruluşlara Yapılan Teslim ve Hizmetler" },
  { code: "314", reason: "19/2 Usulüne Göre Yürürlüğe Girmiş Uluslar Arası Anlaşmalar Kapsamındaki İstisnalar" },
  { code: "315", reason: "14/3 İhraç Konusu Eşyayı Taşıyan Kamyon, Çekici ve Yarı Romorklara Yapılan Motorin Teslimleri" },
  { code: "316", reason: "11/1-a Serbest Bölgelerdeki Müşteriler İçin Yapılan Fason Hizmetler" },
  { code: "317", reason: "17/4-s Engellilerin Eğitimleri, Meslekleri ve Günlük Yaşamlarına İlişkin Araç-Gereç ve Bilgisayar Programları" },
  { code: "318", reason: "Geçici 29 3996 Sayılı Kanuna Göre Yap-İşlet-Devret Modeli Çerçevesinde Gerçekleştirilecek Projeler, 3359 Sayılı Kanuna Göre Kiralama Karşılığı Yaptırılan Sağlık Tesislerine İlişkin Projeler ve 652 Sayılı Kanun Hükmünde Kararnameye Göre Kiralama Karşılığı Yaptırılan Eğitim Öğretim Tesislerine İlişkin Projelere İlişkin Teslim ve Hizmetler" },
  { code: "319", reason: "13/g Başbakanlık Merkez Teşkilatına Yapılan Araç Teslimleri" },
  { code: "320", reason: "Geçici 16 (6111 sayılı K.) İSMEP Kapsamında İstanbul İl Özel İdaresi'ne Bağlı Olarak Faaliyet Gösteren \"İstanbul Proje Koordinasyon Birimi\"ne Yapılacak Teslim ve Hizmetler" },
  { code: "321", reason: "Geçici 26 Birleşmiş Milletler (BM) ile Kuzey Atlantik Antlaşması Teşkilatı (NATO) Temsilcilikleri ve Bu Teşkilatlara Bağlı Program, Fon ve Özel İhtisas Kuruluşları ile İktisadi İşbirliği ve Kalkınma Teşkilatına (OECD) Resmi Kullanımları İçin Yapılacak Mal Teslimi ve Hizmet İfaları, Bunların Sosyal ve Ekonomik Yardım Amacıyla Bedelsiz Olarak Yapacakları Mal Teslimi ve Hizmet İfaları İle İlgili Bunlara Yapılan Mal Teslimi ve Hizmet İfaları" },
  { code: "322", reason: "11/1-a Türkiye'de İkamet Etmeyenlere Özel Fatura ile Yapılan Teslimler (Bavul Ticareti)" },
  { code: "323", reason: "13/ğ 5300 Sayılı Kanuna Göre Düzenlenen Ürün Senetlerinin İhtisas/Ticaret Borsaları Aracılığıyla İlk Teslimi" },
  { code: "324", reason: "13/h Türkiye Kızılay Derneğine Yapılan Teslim ve Hizmetler ile Türkiye Kızılay Derneğinin Teslim ve Hizmetleri" },
  { code: "325", reason: "13/ı Yem Teslimleri" },
  { code: "326", reason: "13/ı Gıda, Tarım ve Hayvancılık Bakanlığı Tarafından Tescil Edilmiş Gübrelerin Teslimi" },
  { code: "327", reason: "13/ı Gıda, Tarım ve Hayvancılık Bakanlığı Tarafından Tescil Edilmiş Gübrelerin İçeriğinde Bulunan Hammaddelerin Gübre Üreticilerine Teslimi" },
  { code: "328", reason: "13/i Konut veya İşyeri Teslimleri" },
  { code: "329", reason: "Eğitimde Fırsatları Artırma ve Teknolojiyi İyileştirme Hareketi (FATİH) projesi Kapsamında Milli Eğitim Bakanlığına Yapılacak Mal Teslimi ve Hizmet İfası" },
  { code: "330", reason: "KDV 13/j md. Organize Sanayi Bölgeleri ile Küçük Sanayi Sitelerinin İnşasına İlişkin Teslim ve Hizmetler" },
  { code: "331", reason: "KDV 13/m md. Ar-Ge, Yenilik ve Tasarım Faaliyetlerinde Kullanılmak Üzere Yapılan Yeni Makina ve Teçhizat Teslimlerinde İstisna" },
  { code: "332", reason: "KDV Geçici 39. Md. İmalat Sanayiinde Kullanılmak Üzere Yapılan Yeni Makina ve Teçhizat Teslimlerinde İstisna" },
  { code: "333", reason: "KDV 13/k md. Kapsamında Genel ve Özel Bütçeli Kamu İdarelerine, İl Özel İdarelerine, Belediyelere ve Köylere bağışlanan Tesislerin İnşasına İlişkin İstisna" },
  { code: "334", reason: "KDV 13/l md. Kapsamında Yabancılara Verilen Sağlık Hizmetlerinde İstisna" },
  { code: "335", reason: "KDV 13/n Basılı Kitap ve Süreli Yayınların Teslimleri" },
  { code: "336", reason: "Geçici 46 UEFA Müsabakaları Kapsamında Yapılacak Teslim ve Hizmetler" },
  { code: "337", reason: "Türk Akım Gaz Boru Hattı Projesine İlişkin Anlaşmanın (9/h) Maddesi Kapsamındaki Gaz Taşıma Hizmetleri" },
  { code: "338", reason: "İmalatçıların Mal İhracatları" },
  { code: "339", reason: "İmalat Sanayii ile Turizme Yönelik Yatırım Teşvik Belgesi Kapsamındaki İnşaat İşlerine İlişkin Teslim ve Hizmetler" },
  { code: "340", reason: "Elektrik Motorlu Taşıt Araçlarının Geliştirilmesine Yönelik Mühendislik Hizmetleri" },
  { code: "341", reason: "Afetzedelere Bağışlanacak Konutların İnşasına İlişkin İstisna" },
  { code: "342", reason: "Genel Bütçeli Kamu İdarelerine Bağışlanacak Taşınmazların İnşasına İlişkin İstisna" },
  { code: "343", reason: "Genel Bütçeli Kamu İdarelerine Bağışlanacak Konutların Yabancı Devlet Kurum ve Kuruluşlarına Teslimine İlişkin İstisna" },
  { code: "344", reason: "13/o Milli Savunma ve İç Güvenlik İhtiyaçlarında Kullanılmak Üzere Taşıt Teslimi" },
  { code: "350", reason: "Diğerleri" },
  { code: "351", reason: "KDV - İstisna Olmayan Diğer" },
  { code: "701", reason: "3065 s. KDV Kanununun 11/1-c md. Kapsamındaki İhraç Kayıtlı Satış" },
  { code: "702", reason: "DİİB ve Geçici Kabul Rejimi Kapsamındaki Satışlar" },
  { code: "703", reason: "4760 s. ÖTV Kanununun 8/2 Md. Kapsamındaki İhraç Kayıtlı Satış" },
  { code: "704", reason: "3065 sayılı KDV Kanununun (11/1-c) maddesi ve 4760 s. Ötv Kanununun 8/2. Md. Kapsamındaki İhraç Kayıtlı Satış" },
  { code: "801", reason: "Milli Piyango, Spor Toto vb. Oyunlar" },
  { code: "802", reason: "At Yarışları ve Diğer Müşterek Bahis ve Talih Oyunları" },
  { code: "803", reason: "Profesyonel Sanatçıların Yer Aldığı Gösteriler, Konserler, Profesyonel Sporcuların Katıldığı Sportif Faaliyetler, Maçlar, Yarışlar ve Yarışmalar" },
  { code: "804", reason: "Gümrük Depolarında ve Müzayede Mahallerinde Yapılan Satışlar" },
  { code: "805", reason: "Altından Mamül veya Altın İçeren Ziynet Eşyaları İle Sikke Altınların Teslimi" },
  { code: "806", reason: "Tütün Mamülleri" },
  { code: "807", reason: "Muzır Neşriyat Kapsamındaki Gazete, Dergi vb. Periyodik Yayınlar" },
  { code: "808", reason: "Gümüşten Mamul veya Gümüş İçeren Ziynet Eşyaları ile Sikke Gümüşlerin Teslimi" },
  { code: "809", reason: "Belediyeler Tarafından Yapılan Şehir İçi Yolcu Taşımacılığında Kullanılan Biletlerin ve Kartların Bayiler Tarafından Satışı" },
  { code: "810", reason: "Ön Ödemeli Elektronik Haberleşme Hizmetleri" },
  { code: "811", reason: "TŞOF Tarafından Araç Plakaları ile Sürücü Kurslarında Kullanılan Bir Kısım Evrakın Teslimi" },
  { code: "812", reason: "KDV Uygulanmadan Alınan İkinci El Motorlu Kara Taşıtı veya Taşınmaz Teslimi" },
];

function FieldLabel({ children, help }) {
  return (
    <Label className="flex items-center gap-2">
      <span>{children}</span>
      {help ? (
        <TooltipProvider delayDuration={120}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-border text-[10px] text-muted-foreground">?</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs leading-5">
              {help}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </Label>
  );
}

function formatVatExemptionOption(option) {
  return `${option.code} - ${option.reason}`;
}

function getVatExemptionDisplay(code, reason) {
  const c = String(code || "").trim();
  const r = String(reason || "").trim();
  if (c && r) return `${c} - ${r}`;
  if (r) return r;
  return c;
}

function parseVatExemptionSelection(value) {
  const raw = String(value || "").trim();
  if (!raw) return { code: "", reason: "" };
  const byReason = VAT_EXEMPTION_OPTIONS.find((x) => x.reason.toLowerCase() === raw.toLowerCase());
  if (byReason) return { code: byReason.code, reason: byReason.reason };
  const matched = VAT_EXEMPTION_OPTIONS.find((x) => formatVatExemptionOption(x) === raw);
  if (matched) return { code: matched.code, reason: matched.reason };
  const codeMatch = raw.match(/^(\d{3})/);
  if (codeMatch) {
    const found = VAT_EXEMPTION_OPTIONS.find((x) => x.code === codeMatch[1]);
    if (found) return { code: found.code, reason: found.reason };
  }
  return { code: "", reason: raw };
}

function normalizeN11VatRate(value) {
  const next = String(value || "");
  return n11VatRates.some((r) => r.value === next) ? next : "20";
}

export default function IntegrationSettingsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [integration, setIntegration] = useState(null);
  const [parasutAccounts, setParasutAccounts] = useState([]);
  const [parasutWarehouses, setParasutWarehouses] = useState([]);
  const [parasutCategories, setParasutCategories] = useState({ contact: [], invoice: [] });
  const [parasutTags, setParasutTags] = useState([]);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [accountingWarehouses, setAccountingWarehouses] = useState([]);
  const [trendyolCargoCompanies, setTrendyolCargoCompanies] = useState([]);
  const [wooMetaKeys, setWooMetaKeys] = useState([]);
  const [wooMetaSamples, setWooMetaSamples] = useState({});
  const [loadingWooMeta, setLoadingWooMeta] = useState(false);
  const [wcStatuses, setWcStatuses] = useState(defaultWcStatuses);
  const [trendyolSheetOpen, setTrendyolSheetOpen] = useState(true);
  const [credentialsGateOpen, setCredentialsGateOpen] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);
  const [verifiedCredentials, setVerifiedCredentials] = useState(null);
  const [n11TaxExemptionSearch, setN11TaxExemptionSearch] = useState("");
  const [trendyolTaxExemptionSearch, setTrendyolTaxExemptionSearch] = useState("");
  
  // API Settings
  const [apiSettings, setApiSettings] = useState({
    store_url: "",
    consumer_key: "",
    consumer_secret: "",
    webhook_secret: "",
  });
  
  // Invoice/Muhasebe Settings
  const [invoiceSettings, setInvoiceSettings] = useState({
    vat_rate: "20",
    invoice_category_id: "",
    customer_category_id: "",
    invoice_tag_id: "",
    auto_create_e_document: false,
    auto_create_invoice: true,
    mark_as_paid: false,
    payment_account_id: "",
    invoice_status: "completed",
    invoice_statuses: ["completed"],
    invoice_note: "",
    add_order_note_to_invoice: false,
    products_only_from_gross_total: false,
    selected_warehouse_id: "",
    international_as_export: false,
    daily_order_summary: false,
    tax_exemption_enabled: false,
    tax_exemption_code: "",
    tax_exemption_reason: "",
  });
  
  // Feature Settings
const [featureSettings, setFeatureSettings] = useState({
  pull_all_orders: true,
  auto_pick_orders: true,
  tax_office_field: "_billing_tax_office",
  tax_number_field: "_billing_tax_number",
  tc_number_field: "_billing_tc_number",
    invoice_note_field: "_billing_invoice_note",
  });
  const [n11Settings, setN11Settings] = useState({
    store_name: "",
    vat_rate: "20",
  });

  const [genericSettings, setGenericSettings] = useState({
    url: "",
    api_key: "",
    api_secret: "",
    username: "",
    password: "",
    seller_id: "",
    vendor_id: "",
  });
  const accountingProviderLabel = accountingProvider === "bizimhesap" ? "BizimHesap" : "Paraşüt";

  const [trendyolSettings, setTrendyolSettings] = useState({
    role_info: "",
    seller_id: "",
    integration_reference_code: "",
    api_key: "",
    api_secret: "",
    token: "",
    use_stage: false,
    pull_all_orders: true,
    auto_pick_orders: true,
    invoice_statuses: ["Invoiced"],
    invoice_status: "Invoiced",
    auto_create_invoice: true,
    auto_create_e_document: false,
    mark_as_paid: false,
    payment_account_id: "",
    invoice_category_id: "",
    customer_category_id: "",
    invoice_tag_id: "",
    invoice_note: "",
    add_order_note_to_invoice: true,
    share_invoices_to_trendyol: true,
    micro_export_auto_invoice: false,
    tax_exemption_enabled: false,
    tax_exemption_code: "",
    tax_exemption_reason: "",
    maturity_difference_days: "",
    create_waybill_invoice: false,
    e_invoice_type: "basic",
    cargo_company: "",
    disable_cargo_for_order: false,
    auto_email_e_invoice: false,
    selected_warehouse_id: "",
  });
  useEffect(() => {
    const selected = VAT_EXEMPTION_OPTIONS.find((x) => x.code === String(invoiceSettings.tax_exemption_code || ""));
    setN11TaxExemptionSearch(selected ? formatVatExemptionOption(selected) : String(invoiceSettings.tax_exemption_code || ""));
  }, [invoiceSettings.tax_exemption_code]);

  useEffect(() => {
    const selected = VAT_EXEMPTION_OPTIONS.find((x) => x.code === String(trendyolSettings.tax_exemption_code || ""));
    setTrendyolTaxExemptionSearch(selected ? formatVatExemptionOption(selected) : String(trendyolSettings.tax_exemption_code || ""));
  }, [trendyolSettings.tax_exemption_code]);
  const [hepsiburadaSettings, setHepsiburadaSettings] = useState({
    merchant_id: "",
    service_key: "",
    vat_rate: "20",
    prices_include_vat: true,
    tax_exemption_enabled: false,
    maturity_difference_days: "",
    create_waybill_invoice: true,
    mark_as_paid: true,
    payment_account_id: "",
    invoice_category_id: "",
    customer_category_id: "",
    invoice_tag_id: "",
    international_as_export: false,
    auto_create_e_document: true,
    e_invoice_type: "basic",
    cargo_company: "HepsiJet",
    disable_cargo_for_order: false,
    auto_email_e_invoice: false,
    invoice_note: "",
    daily_order_summary: false,
    pull_all_orders: true,
    invoice_statuses: ["Shipped", "Delivered"],
    auto_create_invoice: true,
    add_order_note_to_invoice: true,
    share_invoices_to_hepsiburada: true,
    selected_warehouse_id: "",
  });
  const [amazonSettings, setAmazonSettings] = useState({
    store_name: "",
    application_name: "",
    vat_rate: "20",
    amazon_country: "TR",
    application_id: "",
    lwa_client_id: "",
    lwa_client_secret: "",
    refresh_token: "",
    redirect_uri: "",
    region: "tr",
    pull_all_orders: true,
    invoice_statuses: ["Shipped"],
    invoice_status: "Shipped",
    auto_create_invoice: true,
    add_order_note_to_invoice: true,
    mark_as_paid: false,
    payment_account_id: "",
    invoice_category_id: "",
    customer_category_id: "",
    invoice_tag_id: "",
    cargo_company: "AFN - Amazon Lojistik",
    tax_exemption_enabled: false,
    maturity_difference_days: "",
    create_waybill_invoice: false,
    international_as_export: false,
    auto_create_e_document: true,
    e_invoice_type: "basic",
    disable_cargo_for_order: true,
    auto_email_e_invoice: false,
    invoice_note: "",
    daily_order_summary: false,
    seller_flex_enabled: false,
    selected_warehouse_id: "",
  });
  const [amazonAuthLoading, setAmazonAuthLoading] = useState(false);
  const [amazonExchangingCode, setAmazonExchangingCode] = useState(false);

  const loadTrendyolReferenceData = async (integrationId) => {
    try {
      const [cargoRes] = await Promise.allSettled([
        api.get(`/trendyol/${integrationId}/cargo-companies`),
      ]);
      if (cargoRes.status === "fulfilled") {
        setTrendyolCargoCompanies(cargoRes.value?.items || []);
      }
    } catch (_) {}
  };

  const requestCredentialsCode = async () => {
    setVerificationSending(true);
    try {
      const result = await api.post(`/integrations/${id}/credentials/access/request`, {});
      toast.success(result?.message || "Doğrulama kodu e-posta adresinize gönderildi.");
    } catch (error) {
      toast.error(error.message || "Doğrulama kodu gönderilemedi.");
    } finally {
      setVerificationSending(false);
    }
  };

  const handleVerifyCredentialsCode = async () => {
    const code = String(verificationCode || "").trim();
    if (!code) {
      toast.error("Lütfen doğrulama kodunu girin.");
      return;
    }
    setVerificationChecking(true);
    try {
      const verify = await api.post(`/integrations/${id}/credentials/access/verify`, { code });
      const token = verify?.access_token;
      if (!token) throw new Error("Erişim doğrulaması alınamadı.");
      const detail = await api.get(`/integrations/${id}/credentials/secret?access_token=${encodeURIComponent(token)}`);
      setVerifiedCredentials(detail?.credentials || null);
      setCredentialsGateOpen(false);
      setCredentialsModalOpen(true);
      setVerificationCode("");
    } catch (error) {
      toast.error(error.message || "Doğrulama başarısız.");
    } finally {
      setVerificationChecking(false);
    }
  };

  const fetchWooStatuses = useCallback(async (integrationId, selectedStatuses = []) => {
    try {
      const data = await api.get(`/woocommerce/${integrationId}/order-statuses`);
      const items = Array.isArray(data?.items) ? data.items : [];
      if (items.length > 0) {
        const merged = [...items];
        for (const selected of selectedStatuses) {
          if (selected && !merged.some((s) => s.value === selected)) {
            merged.push({ value: selected, label: selected });
          }
        }
        setWcStatuses(merged);
      } else {
        setWcStatuses(defaultWcStatuses);
      }
    } catch (_) {
      setWcStatuses(defaultWcStatuses);
    }
  }, []);

  const fetchWooMetaKeys = async (integrationId) => {
    setLoadingWooMeta(true);
    try {
      const data = await api.get(`/woocommerce/${integrationId}/meta-keys`);
      setWooMetaKeys(data.keys || []);
      setWooMetaSamples(data.sample_values || {});
    } catch (error) {
      setWooMetaKeys([]);
      setWooMetaSamples({});
    } finally {
      setLoadingWooMeta(false);
    }
  };

  const fetchIntegration = useCallback(async () => {
    try {
      const integrations = await api.get("/integrations");
      const found = integrations.find(i => i.id === id);
      if (found) {
        setIntegration(found);
        const creds = found.credentials || {};
        const invSettings = creds.invoice_settings || {};
        if (found.provider === "woocommerce") {
          const hasWooCredentials = Boolean(
            String(creds.store_url || "").trim() &&
            String(creds.consumer_key || "").trim() &&
            String(creds.consumer_secret || "").trim()
          );
          const selectedStatuses = Array.isArray(invSettings.invoice_statuses)
            ? invSettings.invoice_statuses
            : String(invSettings.invoice_status || "completed")
                .split(",")
                .map((v) => String(v || "").trim())
                .filter(Boolean);
          if (hasWooCredentials) {
            fetchWooMetaKeys(found.id);
            fetchWooStatuses(found.id, selectedStatuses);
          } else {
            setWooMetaKeys([]);
            setWooMetaSamples({});
            setWcStatuses(defaultWcStatuses);
          }
        }
        if (found.provider === "trendyol") {
          loadTrendyolReferenceData(found.id);
        }
        
        // Load saved settings
        setApiSettings({
          store_url: creds.store_url || "",
          consumer_key: creds.consumer_key || "",
          consumer_secret: creds.consumer_secret || "",
          webhook_secret: creds.webhook_secret || "",
        });
        
        setInvoiceSettings({
          vat_rate: String(invSettings.vat_rate || "20"),
          invoice_category_id: invSettings.invoice_category_id || "",
          customer_category_id: invSettings.customer_category_id || "",
          invoice_tag_id: invSettings.invoice_tag_id || "",
          auto_create_e_document: invSettings.auto_create_e_document || false,
          auto_create_invoice: invSettings.auto_create_invoice !== false,
          mark_as_paid: invSettings.mark_as_paid || false,
          payment_account_id: invSettings.payment_account_id || "",
          invoice_status: invSettings.invoice_status || "completed",
          invoice_statuses: Array.isArray(invSettings.invoice_statuses)
            ? invSettings.invoice_statuses
            : String(invSettings.invoice_status || "completed")
                .split(",")
                .map((v) => String(v || "").trim())
                .filter(Boolean),
          invoice_note: invSettings.invoice_note || "",
          add_order_note_to_invoice: invSettings.add_order_note_to_invoice || false,
          products_only_from_gross_total: invSettings.products_only_from_gross_total || false,
          selected_warehouse_id: invSettings.selected_warehouse_id || "",
          international_as_export: Boolean(invSettings.international_as_export || false),
          daily_order_summary: Boolean(invSettings.daily_order_summary || false),
          tax_exemption_enabled: Boolean(invSettings.tax_exemption_enabled || false),
          tax_exemption_code: invSettings.tax_exemption_code || "",
          tax_exemption_reason: invSettings.tax_exemption_reason || "",
        });
        setN11Settings({
          store_name: creds.store_name || found.name || "",
          vat_rate: String(invSettings.vat_rate || "20"),
        });
        
        setFeatureSettings({
          pull_all_orders: invSettings.pull_all_orders !== false,
          auto_pick_orders: invSettings.auto_pick_orders !== false,
          tax_office_field: invSettings.tax_office_field || "_billing_tax_office",
          tax_number_field: invSettings.tax_number_field || "_billing_tax_number",
          tc_number_field: invSettings.tc_number_field || "_billing_tc_number",
          invoice_note_field: invSettings.invoice_note_field || "_billing_invoice_note",
        });

        setGenericSettings({
          url: creds.url || creds.store_url || "",
          api_key: creds.api_key || creds.consumer_key || "",
          api_secret: creds.api_secret || creds.consumer_secret || "",
          username: creds.username || "",
          password: creds.password || "",
          seller_id: creds.seller_id || "",
          vendor_id: creds.vendor_id || "",
        });
        setTrendyolSettings({
          role_info: creds.role_info || "",
          seller_id: creds.seller_id || "",
          integration_reference_code: creds.integration_reference_code || "",
          api_key: creds.api_key || "",
          api_secret: creds.api_secret || "",
          token: creds.token || "",
          use_stage: Boolean(creds.use_stage || false),
          pull_all_orders: creds?.invoice_settings?.pull_all_orders !== false,
          auto_pick_orders: creds?.invoice_settings?.auto_pick_orders !== false,
          invoice_statuses: Array.isArray(creds?.invoice_settings?.invoice_statuses)
            ? creds.invoice_settings.invoice_statuses
            : String(creds?.invoice_settings?.invoice_status || "Invoiced")
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
          invoice_status: creds?.invoice_settings?.invoice_status || "Invoiced",
          auto_create_invoice: creds?.invoice_settings?.auto_create_invoice !== false,
          auto_create_e_document: Boolean(creds?.invoice_settings?.auto_create_e_document || false),
          mark_as_paid: Boolean(creds?.invoice_settings?.mark_as_paid || false),
          payment_account_id: creds?.invoice_settings?.payment_account_id || "",
          invoice_category_id: creds?.invoice_settings?.invoice_category_id || "",
          customer_category_id: creds?.invoice_settings?.customer_category_id || "",
          invoice_tag_id: creds?.invoice_settings?.invoice_tag_id || "",
          invoice_note: creds?.invoice_settings?.invoice_note || "",
          add_order_note_to_invoice: creds?.invoice_settings?.add_order_note_to_invoice !== false,
          share_invoices_to_trendyol: creds?.invoice_settings?.share_invoices_to_trendyol !== false,
          micro_export_auto_invoice: Boolean(creds?.invoice_settings?.micro_export_auto_invoice || false),
          tax_exemption_enabled: Boolean(creds?.invoice_settings?.tax_exemption_enabled || false),
          tax_exemption_code: creds?.invoice_settings?.tax_exemption_code || "",
          tax_exemption_reason: creds?.invoice_settings?.tax_exemption_reason || "",
          maturity_difference_days: String(creds?.invoice_settings?.maturity_difference_days || ""),
          create_waybill_invoice: Boolean(creds?.invoice_settings?.create_waybill_invoice || false),
          e_invoice_type: creds?.invoice_settings?.e_invoice_type || "basic",
          cargo_company: creds?.invoice_settings?.cargo_company || "",
          disable_cargo_for_order: Boolean(creds?.invoice_settings?.disable_cargo_for_order || false),
          auto_email_e_invoice: Boolean(creds?.invoice_settings?.auto_email_e_invoice || false),
          selected_warehouse_id: creds?.invoice_settings?.selected_warehouse_id || "",
        });
        setHepsiburadaSettings({
          merchant_id: creds.merchant_id || "",
          service_key: creds.service_key || creds.password || "",
          vat_rate: String(creds?.invoice_settings?.vat_rate || "20"),
          prices_include_vat: creds?.invoice_settings?.prices_include_vat !== false,
          tax_exemption_enabled: Boolean(creds?.invoice_settings?.tax_exemption_enabled || false),
          maturity_difference_days: String(creds?.invoice_settings?.maturity_difference_days || ""),
          create_waybill_invoice: creds?.invoice_settings?.create_waybill_invoice !== false,
          mark_as_paid: creds?.invoice_settings?.mark_as_paid !== false,
          payment_account_id: creds?.invoice_settings?.payment_account_id || "",
          invoice_category_id: creds?.invoice_settings?.invoice_category_id || "",
          customer_category_id: creds?.invoice_settings?.customer_category_id || "",
          invoice_tag_id: creds?.invoice_settings?.invoice_tag_id || "",
          international_as_export: Boolean(creds?.invoice_settings?.international_as_export || false),
          auto_create_e_document: creds?.invoice_settings?.auto_create_e_document !== false,
          e_invoice_type: creds?.invoice_settings?.e_invoice_type || "basic",
          cargo_company: creds?.invoice_settings?.cargo_company || "HepsiJet",
          disable_cargo_for_order: Boolean(creds?.invoice_settings?.disable_cargo_for_order || false),
          auto_email_e_invoice: Boolean(creds?.invoice_settings?.auto_email_e_invoice || false),
          invoice_note: creds?.invoice_settings?.invoice_note || "",
          daily_order_summary: Boolean(creds?.invoice_settings?.daily_order_summary || false),
          pull_all_orders: creds?.invoice_settings?.pull_all_orders !== false,
          invoice_statuses: Array.isArray(creds?.invoice_settings?.invoice_statuses)
            ? creds.invoice_settings.invoice_statuses
            : ["Shipped", "Delivered"],
          auto_create_invoice: creds?.invoice_settings?.auto_create_invoice !== false,
          add_order_note_to_invoice: creds?.invoice_settings?.add_order_note_to_invoice !== false,
          share_invoices_to_hepsiburada: creds?.invoice_settings?.share_invoices_to_hepsiburada !== false,
          selected_warehouse_id: creds?.invoice_settings?.selected_warehouse_id || "",
        });
        setAmazonSettings({
          store_name: creds.store_name || found.name || "",
          application_name: creds.application_name || "",
          vat_rate: String(creds?.invoice_settings?.vat_rate || "20"),
          amazon_country: String((creds.region || "tr")).toUpperCase() === "TR" ? "TR" : String((creds.region || "tr")).toUpperCase(),
          application_id: creds.application_id || "",
          lwa_client_id: creds.lwa_client_id || "",
          lwa_client_secret: creds.lwa_client_secret || "",
          refresh_token: creds.refresh_token || "",
          redirect_uri: creds.redirect_uri || "",
          region: String(creds.region || "tr").toLowerCase(),
          pull_all_orders: creds?.invoice_settings?.pull_all_orders !== false,
          invoice_statuses: Array.isArray(creds?.invoice_settings?.invoice_statuses)
            ? creds.invoice_settings.invoice_statuses
            : ["Shipped"],
          invoice_status: creds?.invoice_settings?.invoice_status || "Shipped",
          auto_create_invoice: creds?.invoice_settings?.auto_create_invoice !== false,
          add_order_note_to_invoice: creds?.invoice_settings?.add_order_note_to_invoice !== false,
          mark_as_paid: Boolean(creds?.invoice_settings?.mark_as_paid || false),
          payment_account_id: creds?.invoice_settings?.payment_account_id || "",
          invoice_category_id: creds?.invoice_settings?.invoice_category_id || "",
          customer_category_id: creds?.invoice_settings?.customer_category_id || "",
          invoice_tag_id: creds?.invoice_settings?.invoice_tag_id || "",
          cargo_company: creds?.invoice_settings?.cargo_company || "AFN - Amazon Lojistik",
          tax_exemption_enabled: Boolean(creds?.invoice_settings?.tax_exemption_enabled || false),
          maturity_difference_days: String(creds?.invoice_settings?.maturity_difference_days || ""),
          create_waybill_invoice: Boolean(creds?.invoice_settings?.create_waybill_invoice || false),
          international_as_export: Boolean(creds?.invoice_settings?.international_as_export || false),
          auto_create_e_document: creds?.invoice_settings?.auto_create_e_document !== false,
          e_invoice_type: creds?.invoice_settings?.e_invoice_type || "basic",
          disable_cargo_for_order: creds?.invoice_settings?.disable_cargo_for_order !== false,
          auto_email_e_invoice: Boolean(creds?.invoice_settings?.auto_email_e_invoice || false),
          invoice_note: creds?.invoice_settings?.invoice_note || "",
          daily_order_summary: Boolean(creds?.invoice_settings?.daily_order_summary || false),
          seller_flex_enabled: Boolean(creds?.invoice_settings?.seller_flex_enabled || false),
          selected_warehouse_id: creds?.invoice_settings?.selected_warehouse_id || "",
        });
      }
    } catch (error) {
      toast.error("Entegrasyon bilgileri alınamadı");
    } finally {
      setLoading(false);
    }
  }, [id, fetchWooStatuses]);

  const fetchAccountingData = useCallback(async () => {
    try {
      const settings = await api.get("/settings/accounting");
      const preferred = String(settings?.provider || settings?.preferred_provider || "").toLowerCase();
      const isParasutConnected = Boolean(settings?.parasut?.connected);
      const isBizimhesapConnected = Boolean(settings?.bizimhesap?.connected);
      const activeProvider =
        preferred === "bizimhesap" || preferred === "parasut"
          ? preferred
          : isBizimhesapConnected
            ? "bizimhesap"
            : isParasutConnected
              ? "parasut"
              : "";

      setAccountingProvider(activeProvider);

      if (activeProvider === "bizimhesap" && isBizimhesapConnected) {
        setParasutAccounts([]);
        setParasutTags([]);
        setParasutWarehouses([]);
        setParasutCategories({ contact: [], invoice: [] });
        const rows = await api.get("/bizimhesap/warehouses").catch(() => ({}));
        const list = Array.isArray(rows?.warehouses) ? rows.warehouses : Array.isArray(rows) ? rows : [];
        const normalized = list
          .map((wh) => ({ id: String(wh?.id || wh?.warehouseId || wh?.warehouse_id || ""), name: wh?.name || wh?.title || "" }))
          .filter((wh) => wh.id);
        setAccountingWarehouses(normalized);
        return;
      }

      if (activeProvider === "parasut" && isParasutConnected) {
        const data = await api.get("/parasut/reference-data");
        const warehouses = Array.isArray(data?.warehouses) ? data.warehouses : [];
        setParasutAccounts(Array.isArray(data?.accounts) ? data.accounts : []);
        setParasutTags(Array.isArray(data?.tags) ? data.tags : []);
        setParasutWarehouses(warehouses);
        setParasutCategories({
          contact: Array.isArray(data?.contact_categories) ? data.contact_categories : [],
          invoice: Array.isArray(data?.invoice_categories) ? data.invoice_categories : [],
        });
        setAccountingWarehouses(warehouses);
        return;
      }

      setParasutAccounts([]);
      setParasutTags([]);
      setParasutWarehouses([]);
      setParasutCategories({ contact: [], invoice: [] });
      setAccountingWarehouses([]);
    } catch (error) {
      console.log("Accounting data fetch error:", error);
      setAccountingProvider("");
      setParasutAccounts([]);
      setParasutTags([]);
      setParasutWarehouses([]);
      setParasutCategories({ contact: [], invoice: [] });
      setAccountingWarehouses([]);
    }
  }, []);

  useEffect(() => {
    fetchIntegration();
    fetchAccountingData();
  }, [fetchIntegration, fetchAccountingData]);

  useEffect(() => {
    if (!integration || integration.provider !== "amazon" || amazonExchangingCode) return;
    const params = new URLSearchParams(window.location.search);
    const authCode = params.get("spapi_oauth_code") || params.get("code");
    if (!authCode) return;
    const state = params.get("state") || undefined;
    const sellingPartnerId = params.get("selling_partner_id") || params.get("sellingPartnerId") || undefined;
    setAmazonExchangingCode(true);
    (async () => {
      try {
        await api.post(`/amazon/${id}/exchange-code`, {
          code: authCode,
          state,
          selling_partner_id: sellingPartnerId,
          redirect_uri: amazonSettings.redirect_uri || undefined,
        });
        toast.success("Amazon doğrulaması tamamlandı");
        await fetchIntegration();
      } catch (error) {
        toast.error(error.message || "Amazon doğrulama kodu alınamadı");
      } finally {
        setAmazonExchangingCode(false);
        navigate(`/panel/entegrasyonlar/${id}/ayarlar`, { replace: true });
      }
    })();
  }, [integration, id, amazonSettings.redirect_uri, amazonExchangingCode, navigate, fetchIntegration]);

  const resolveWarehouseName = useCallback((warehouseId) => {
    if (!warehouseId) return "";
    const found = (accountingWarehouses || []).find((w) => String(w.id) === String(warehouseId));
    return found?.name || "";
  }, [accountingWarehouses]);

  const handleTestConnection = async () => {
    if (!apiSettings.store_url || !apiSettings.consumer_key || !apiSettings.consumer_secret) {
      toast.error("Lütfen API bilgilerini doldurun");
      return;
    }
    
    setTesting(true);
    try {
      const result = await api.post("/woocommerce/test", apiSettings);
      if (result.success) {
        try {
          await api.patch(`/integrations/${id}`, { status: "success", error: null });
          setIntegration((prev) => (prev ? { ...prev, status: "success" } : prev));
        } catch (_) {}
        toast.success(result.message || "Bağlantı başarılı!");
      } else {
        toast.error(result.message || "Bağlantı hatası");
      }
    } catch (error) {
      toast.error(error.message || "Bağlantı test edilemedi");
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save API credentials
      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...apiSettings,
          cargo_provider: integration?.credentials?.cargo_provider,
          cargo_name: integration?.credentials?.cargo_name,
          invoice_settings: {
            ...invoiceSettings,
            ...featureSettings,
            vat_rate: parseFloat(invoiceSettings.vat_rate),
            selected_warehouse_name: resolveWarehouseName(invoiceSettings.selected_warehouse_id),
          }
        }
      });
      
      toast.success("Ayarlar kaydedildi!");
    } catch (error) {
      toast.error(error.message || "Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncOrders = async () => {
    setSyncing(true);
    try {
      const preferredStatus = (
        Array.isArray(invoiceSettings.invoice_statuses) && invoiceSettings.invoice_statuses.length
          ? invoiceSettings.invoice_statuses[0]
          : invoiceSettings.invoice_status
      ) || "completed";
      const result = await api.post(`/woocommerce/${id}/sync-orders?status=${preferredStatus}`);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message || "Senkronizasyon hatası");
      }
    } catch (error) {
      toast.error(error.message || "Siparişler senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handleGenericSave = async () => {
    setSaving(true);
    try {
      const isN11 = integration?.provider === "n11";
      const isPazarama = integration?.provider === "pazarama";
      const isIdefix = integration?.provider === "idefix";
      const apiKey = (genericSettings.api_key || genericSettings.username || "").trim();
      const apiSecret = (genericSettings.api_secret || genericSettings.password || "").trim();

      if (isPazarama) {
        const sellerId = genericSettings.seller_id.trim();
        if (!apiKey || !apiSecret || !sellerId) {
          toast.error("API Key, API Secret ve Satıcı ID zorunludur.");
          setSaving(false);
          return;
        }
        try {
          await api.post("/pazarama/save-config", {
            api_key: apiKey,
            api_secret: apiSecret,
            seller_id: sellerId,
            use_stage: false,
          });
          toast.success("Pazarama başarıyla bağlandı!");
          await fetchIntegration();
        } catch (e) {
          toast.error(e.message || "Pazarama bağlantısı başarısız");
        } finally {
          setSaving(false);
        }
        return;
      }

      if (isIdefix) {
        const vendorId = (genericSettings.vendor_id || "").trim();
        if (!apiKey || !apiSecret || !vendorId) {
          toast.error("Token, Secret ve Satıcı ID zorunludur.");
          setSaving(false);
          return;
        }
        try {
          await api.post("/idefix/save-config", {
            api_key: apiKey,
            api_secret: apiSecret,
            vendor_id: vendorId,
            use_stage: false,
          });
          toast.success("Idefix başarıyla bağlandı!");
          await fetchIntegration();
        } catch (e) {
          toast.error(e.message || "Idefix bağlantısı başarısız");
        } finally {
          setSaving(false);
        }
        return;
      }

      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...integration?.credentials,
          ...(isN11
            ? {
                app_key: apiKey,
                app_secret: apiSecret,
                api_key: apiKey,
                api_secret: apiSecret,
                store_name: n11Settings.store_name || integration?.name || "",
                invoice_settings: {
                  ...(integration?.credentials?.invoice_settings || {}),
                  ...invoiceSettings,
                  ...featureSettings,
                  vat_rate: parseFloat(normalizeN11VatRate(n11Settings.vat_rate || invoiceSettings.vat_rate)),
                  selected_warehouse_name: resolveWarehouseName(invoiceSettings.selected_warehouse_id),
                },
              }
            : genericSettings),
        },
      });
      toast.success("Ayarlar kaydedildi!");
    } catch (error) {
      toast.error(error.message || "Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTrendyolTest = async () => {
    if (!trendyolSettings.seller_id || !trendyolSettings.api_key || !trendyolSettings.api_secret) {
      toast.error("Satıcı ID, API Key ve API Secret zorunludur");
      return;
    }
    setTesting(true);
    try {
      const result = await api.post("/trendyol/test", {
        role_info: trendyolSettings.role_info || null,
        seller_id: trendyolSettings.seller_id,
        integration_reference_code: trendyolSettings.integration_reference_code || null,
        api_key: trendyolSettings.api_key,
        api_secret: trendyolSettings.api_secret,
        token: trendyolSettings.token || null,
        use_stage: trendyolSettings.use_stage,
      });
      if (result?.success) {
        await api.patch(`/integrations/${id}`, {
          status: "success",
          error: null,
          credentials: {
            ...integration?.credentials,
            role_info: trendyolSettings.role_info || "",
            seller_id: trendyolSettings.seller_id,
            integration_reference_code: trendyolSettings.integration_reference_code || "",
            api_key: trendyolSettings.api_key,
            api_secret: trendyolSettings.api_secret,
            token: trendyolSettings.token || "",
            use_stage: trendyolSettings.use_stage,
          },
        });
        setIntegration((prev) => (prev ? { ...prev, status: "success", error: null } : prev));
        await loadTrendyolReferenceData(id);
        toast.success(result.message || "Trendyol bağlantısı başarılı");
      } else {
        try {
          await api.patch(`/integrations/${id}`, { status: "failed", error: result?.message || "Trendyol bağlantı testi başarısız" });
          setIntegration((prev) => (prev ? { ...prev, status: "failed", error: result?.message || "Trendyol bağlantı testi başarısız" } : prev));
        } catch (_) {}
        toast.error(result?.message || "Trendyol bağlantı testi başarısız");
      }
    } catch (error) {
      try {
        await api.patch(`/integrations/${id}`, { status: "failed", error: error.message || "Trendyol bağlantı testi başarısız" });
        setIntegration((prev) => (prev ? { ...prev, status: "failed", error: error.message || "Trendyol bağlantı testi başarısız" } : prev));
      } catch (_) {}
      toast.error(error.message || "Trendyol bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const handleTrendyolSave = async () => {
    setSaving(true);
    try {
      const normalizedStatuses = (trendyolSettings.invoice_statuses || [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      const invoiceStatus = normalizedStatuses[0] || "Invoiced";

      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...integration?.credentials,
          role_info: trendyolSettings.role_info || "",
          seller_id: trendyolSettings.seller_id,
          integration_reference_code: trendyolSettings.integration_reference_code || "",
          api_key: trendyolSettings.api_key,
          api_secret: trendyolSettings.api_secret,
          token: trendyolSettings.token || "",
          use_stage: trendyolSettings.use_stage,
          invoice_settings: {
            ...(integration?.credentials?.invoice_settings || {}),
            pull_all_orders: trendyolSettings.pull_all_orders,
            auto_pick_orders: trendyolSettings.auto_pick_orders,
            auto_create_invoice: trendyolSettings.auto_create_invoice,
            invoice_statuses: normalizedStatuses,
            invoice_status: invoiceStatus,
            auto_create_e_document: trendyolSettings.auto_create_e_document,
            mark_as_paid: trendyolSettings.mark_as_paid,
            payment_account_id: trendyolSettings.payment_account_id || "",
            invoice_category_id: trendyolSettings.invoice_category_id || "",
            customer_category_id: trendyolSettings.customer_category_id || "",
            invoice_tag_id: trendyolSettings.invoice_tag_id || "",
            invoice_note: trendyolSettings.invoice_note || "",
            add_order_note_to_invoice: trendyolSettings.add_order_note_to_invoice,
            share_invoices_to_trendyol: trendyolSettings.share_invoices_to_trendyol,
            micro_export_auto_invoice: trendyolSettings.micro_export_auto_invoice,
            tax_exemption_enabled: trendyolSettings.tax_exemption_enabled,
            tax_exemption_code: trendyolSettings.tax_exemption_code || "",
            tax_exemption_reason: trendyolSettings.tax_exemption_reason || "",
            maturity_difference_days: Number(trendyolSettings.maturity_difference_days || 0),
            create_waybill_invoice: trendyolSettings.create_waybill_invoice,
            e_invoice_type: trendyolSettings.e_invoice_type,
            cargo_company: trendyolSettings.cargo_company || "",
            disable_cargo_for_order: trendyolSettings.disable_cargo_for_order,
            auto_email_e_invoice: trendyolSettings.auto_email_e_invoice,
            selected_warehouse_id: trendyolSettings.selected_warehouse_id || "",
            selected_warehouse_name: resolveWarehouseName(trendyolSettings.selected_warehouse_id),
          },
        },
      });
      toast.success("Trendyol ayarları kaydedildi");
    } catch (error) {
      toast.error(error.message || "Trendyol ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTrendyolSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/trendyol/${id}/sync-orders?full=true&all_statuses=true`);
      if (result?.success) {
        toast.success(result.message || "Trendyol siparişleri senkronize edildi");
      } else {
        toast.error(result?.message || "Trendyol senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.message || "Trendyol siparişleri senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handleHepsiburadaTest = async () => {
    if (!hepsiburadaSettings.merchant_id || !hepsiburadaSettings.service_key) {
      toast.error("Merchant ID ve Servis Anahtarı zorunludur");
      return;
    }
    setTesting(true);
    try {
      const result = await api.post("/hepsiburada/test", {
        merchant_id: hepsiburadaSettings.merchant_id,
        service_key: hepsiburadaSettings.service_key,
      });
      if (result?.success) {
        await api.patch(`/integrations/${id}`, {
          status: "success",
          error: null,
          credentials: {
            ...(integration?.credentials || {}),
            merchant_id: hepsiburadaSettings.merchant_id,
            service_key: hepsiburadaSettings.service_key,
          },
        });
        setIntegration((prev) => (prev ? { ...prev, status: "success", error: null } : prev));
        toast.success(result.message || "Hepsiburada bağlantısı başarılı");
      } else {
        toast.error(result?.message || "Hepsiburada bağlantı testi başarısız");
      }
    } catch (error) {
      toast.error(error.message || "Hepsiburada bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const handleHepsiburadaSave = async () => {
    setSaving(true);
    try {
      const normalizedStatuses = (hepsiburadaSettings.invoice_statuses || [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...(integration?.credentials || {}),
          merchant_id: hepsiburadaSettings.merchant_id,
          service_key: hepsiburadaSettings.service_key,
          invoice_settings: {
            ...(integration?.credentials?.invoice_settings || {}),
            vat_rate: parseFloat(hepsiburadaSettings.vat_rate || "20"),
            prices_include_vat: hepsiburadaSettings.prices_include_vat !== false,
            tax_exemption_enabled: hepsiburadaSettings.tax_exemption_enabled,
            maturity_difference_days: Number(hepsiburadaSettings.maturity_difference_days || 0),
            create_waybill_invoice: hepsiburadaSettings.create_waybill_invoice,
            mark_as_paid: hepsiburadaSettings.mark_as_paid,
            payment_account_id: hepsiburadaSettings.payment_account_id || "",
            invoice_category_id: hepsiburadaSettings.invoice_category_id || "",
            customer_category_id: hepsiburadaSettings.customer_category_id || "",
            invoice_tag_id: hepsiburadaSettings.invoice_tag_id || "",
            international_as_export: hepsiburadaSettings.international_as_export,
            auto_create_e_document: hepsiburadaSettings.auto_create_e_document,
            e_invoice_type: hepsiburadaSettings.e_invoice_type || "basic",
            cargo_company: hepsiburadaSettings.cargo_company || "",
            disable_cargo_for_order: hepsiburadaSettings.disable_cargo_for_order,
            auto_email_e_invoice: hepsiburadaSettings.auto_email_e_invoice,
            invoice_note: hepsiburadaSettings.invoice_note || "",
            daily_order_summary: hepsiburadaSettings.daily_order_summary,
            pull_all_orders: hepsiburadaSettings.pull_all_orders,
            auto_create_invoice: hepsiburadaSettings.auto_create_invoice,
            invoice_statuses: normalizedStatuses,
            invoice_status: normalizedStatuses[0] || "Delivered",
            add_order_note_to_invoice: hepsiburadaSettings.add_order_note_to_invoice,
            share_invoices_to_hepsiburada: hepsiburadaSettings.share_invoices_to_hepsiburada,
            selected_warehouse_id: hepsiburadaSettings.selected_warehouse_id || "",
            selected_warehouse_name: resolveWarehouseName(hepsiburadaSettings.selected_warehouse_id),
          },
        },
      });
      toast.success("Hepsiburada ayarları kaydedildi");
    } catch (error) {
      toast.error(error.message || "Hepsiburada ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleHepsiburadaSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/hepsiburada/${id}/sync-orders?all_statuses=true`);
      if (result?.success) {
        toast.success(result.message || "Hepsiburada siparişleri senkronize edildi");
      } else {
        toast.error(result?.message || "Hepsiburada senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.message || "Hepsiburada siparişleri senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handleN11Test = async () => {
    const appKey = (genericSettings.api_key || genericSettings.username || "").trim();
    const appSecret = (genericSettings.api_secret || genericSettings.password || "").trim();
    if (!appKey || !appSecret) {
      toast.error("N11 için API Key ve API Secret zorunludur");
      return;
    }
    setTesting(true);
    try {
      const result = await api.post("/n11/test", {
        app_key: appKey,
        app_secret: appSecret,
      });
      if (result?.success) {
        await api.patch(`/integrations/${id}`, {
          status: "success",
          error: null,
          credentials: {
            ...(integration?.credentials || {}),
            app_key: appKey,
            app_secret: appSecret,
            api_key: appKey,
            api_secret: appSecret,
          },
        });
        setIntegration((prev) => (prev ? { ...prev, status: "success", error: null } : prev));
        toast.success(result.message || "N11 bağlantısı başarılı");
      } else {
        await api.patch(`/integrations/${id}`, {
          status: "failed",
          error: result?.message || "N11 bağlantı testi başarısız",
        }).catch(() => {});
        toast.error(result?.message || "N11 bağlantı testi başarısız");
      }
    } catch (error) {
      toast.error(error.message || "N11 bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const handleN11Sync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/n11/${id}/sync-orders?days=30&all_statuses=true`);
      if (result?.success) {
        toast.success(result.message || "N11 siparişleri senkronize edildi");
      } else {
        toast.error(result?.message || "N11 senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.message || "N11 siparişleri senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handlePazaramaTest = async () => {
    const apiKey = genericSettings.api_key.trim();
    const apiSecret = genericSettings.api_secret.trim();
    const sellerId = genericSettings.seller_id.trim();
    if (!apiKey || !apiSecret || !sellerId) {
      toast.error("API Key, API Secret ve Satıcı ID zorunludur");
      return;
    }
    setTesting(true);
    try {
      await api.post("/pazarama/save-config", { api_key: apiKey, api_secret: apiSecret, seller_id: sellerId, use_stage: false });
      setIntegration((prev) => (prev ? { ...prev, status: "success", error: null } : prev));
      toast.success("Pazarama bağlantısı başarılı");
    } catch (error) {
      toast.error(error.message || "Pazarama bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const handlePazaramaSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/pazarama/${id}/sync-orders`, { days_back: 30 });
      if (result?.success) {
        toast.success(result.message || "Pazarama siparişleri senkronize edildi");
      } else {
        toast.error(result?.message || "Pazarama senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.message || "Pazarama siparişleri senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handlePazaramaSave = async () => {
    const apiKey = genericSettings.api_key.trim();
    const apiSecret = genericSettings.api_secret.trim();
    const sellerId = genericSettings.seller_id.trim();

    setSaving(true);
    try {
      if (!integration?.status || integration.status !== "success") {
        if (!apiKey || !apiSecret || !sellerId) {
          toast.error("API Key, API Secret ve Satıcı ID zorunludur");
          return;
        }
        await api.post("/pazarama/save-config", {
          api_key: apiKey,
          api_secret: apiSecret,
          seller_id: sellerId,
          use_stage: false,
        });
      }

      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...(integration?.credentials || {}),
          api_key: apiKey || integration?.credentials?.api_key || "",
          api_secret: apiSecret || integration?.credentials?.api_secret || "",
          seller_id: sellerId || integration?.credentials?.seller_id || "",
          invoice_settings: {
            ...(integration?.credentials?.invoice_settings || {}),
            pull_all_orders: featureSettings.pull_all_orders,
            auto_create_invoice: invoiceSettings.auto_create_invoice,
            auto_create_e_document: invoiceSettings.auto_create_e_document,
            mark_as_paid: invoiceSettings.mark_as_paid,
            international_as_export: invoiceSettings.international_as_export,
            daily_order_summary: invoiceSettings.daily_order_summary,
            invoice_note: invoiceSettings.invoice_note || "",
            payment_account_id: invoiceSettings.payment_account_id || "",
            invoice_category_id: invoiceSettings.invoice_category_id || "",
            customer_category_id: invoiceSettings.customer_category_id || "",
            invoice_tag_id: invoiceSettings.invoice_tag_id || "",
            selected_warehouse_id: invoiceSettings.selected_warehouse_id || "",
            selected_warehouse_name: resolveWarehouseName(invoiceSettings.selected_warehouse_id),
            vat_rate: parseFloat(invoiceSettings.vat_rate || "20"),
          },
        },
      });

      await fetchIntegration();
      toast.success("Pazarama ayarları kaydedildi");
    } catch (error) {
      toast.error(error.message || "Pazarama ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleIdefixTest = async () => {
    const apiKey = genericSettings.api_key.trim();
    const apiSecret = genericSettings.api_secret.trim();
    const vendorId = genericSettings.vendor_id.trim();
    if (!apiKey || !apiSecret || !vendorId) {
      toast.error("Token, Secret ve Satıcı ID zorunludur");
      return;
    }
    setTesting(true);
    try {
      await api.post("/idefix/save-config", {
        api_key: apiKey,
        api_secret: apiSecret,
        vendor_id: vendorId,
        use_stage: false,
      });
      setIntegration((prev) => (prev ? { ...prev, status: "success", error: null } : prev));
      toast.success("Idefix bağlantısı başarılı");
    } catch (error) {
      toast.error(error.message || "Idefix bağlantı testi başarısız");
    } finally {
      setTesting(false);
    }
  };

  const handleIdefixSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post(`/idefix/${id}/sync-orders`, { days_back: 30 });
      if (result?.success) {
        toast.success(result.message || "Idefix siparişleri senkronize edildi");
      } else {
        toast.error(result?.message || "Idefix senkronizasyonu başarısız");
      }
    } catch (error) {
      toast.error(error.message || "Idefix siparişleri senkronize edilemedi");
    } finally {
      setSyncing(false);
    }
  };

  const handleIdefixSave = async () => {
    const apiKey = genericSettings.api_key.trim();
    const apiSecret = genericSettings.api_secret.trim();
    const vendorId = genericSettings.vendor_id.trim();
    const normalizedStatuses = (invoiceSettings.invoice_statuses || [])
      .map((s) => String(s || "").trim())
      .filter(Boolean);

    setSaving(true);
    try {
      if (!integration?.status || integration.status !== "success") {
        if (!apiKey || !apiSecret || !vendorId) {
          toast.error("Token, Secret ve Satıcı ID zorunludur");
          return;
        }
        await api.post("/idefix/save-config", {
          api_key: apiKey,
          api_secret: apiSecret,
          vendor_id: vendorId,
          use_stage: false,
        });
      }

      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...(integration?.credentials || {}),
          api_key: apiKey || integration?.credentials?.api_key || "",
          api_secret: apiSecret || integration?.credentials?.api_secret || "",
          vendor_id: vendorId || integration?.credentials?.vendor_id || "",
          invoice_settings: {
            ...(integration?.credentials?.invoice_settings || {}),
            pull_all_orders: featureSettings.pull_all_orders,
            auto_pick_orders: featureSettings.auto_pick_orders,
            auto_create_invoice: invoiceSettings.auto_create_invoice,
            invoice_statuses: normalizedStatuses,
            invoice_status: normalizedStatuses[0] || "shipment_in_cargo",
            auto_create_e_document: invoiceSettings.auto_create_e_document,
            mark_as_paid: invoiceSettings.mark_as_paid,
            international_as_export: invoiceSettings.international_as_export,
            daily_order_summary: invoiceSettings.daily_order_summary,
            invoice_note: invoiceSettings.invoice_note || "",
            payment_account_id: invoiceSettings.payment_account_id || "",
            invoice_category_id: invoiceSettings.invoice_category_id || "",
            customer_category_id: invoiceSettings.customer_category_id || "",
            invoice_tag_id: invoiceSettings.invoice_tag_id || "",
            selected_warehouse_id: invoiceSettings.selected_warehouse_id || "",
            selected_warehouse_name: resolveWarehouseName(invoiceSettings.selected_warehouse_id),
            vat_rate: parseFloat(invoiceSettings.vat_rate || "20"),
          },
        },
      });

      await fetchIntegration();
      toast.success("Idefix ayarları kaydedildi");
    } catch (error) {
      toast.error(error.message || "Idefix ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleAmazonStartAuth = async () => {
    setAmazonAuthLoading(true);
    try {
      const regionMap = { TR: "tr", EU: "eu", US: "us" };
      const result = await api.post(`/amazon/${id}/begin-auth`, {
        region: regionMap[String(amazonSettings.amazon_country || "TR").toUpperCase()] || "tr",
        application_id: amazonSettings.application_id || undefined,
        lwa_client_id: amazonSettings.lwa_client_id || undefined,
        lwa_client_secret: amazonSettings.lwa_client_secret || undefined,
        redirect_uri: amazonSettings.redirect_uri || undefined,
      });
      const authUrl = result?.auth_url;
      if (!authUrl) throw new Error("Amazon yetkilendirme bağlantısı oluşturulamadı");
      window.location.href = authUrl;
    } catch (error) {
      toast.error(error.message || "Amazon yetkilendirmesi başlatılamadı");
    } finally {
      setAmazonAuthLoading(false);
    }
  };

  const handleAmazonSave = async () => {
    setSaving(true);
    try {
      const normalizedStatuses = (amazonSettings.invoice_statuses || [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      const regionMap = { TR: "tr", EU: "eu", US: "us" };
      await api.patch(`/integrations/${id}`, {
        credentials: {
          ...(integration?.credentials || {}),
          ...(amazonSettings.application_id ? { application_id: amazonSettings.application_id } : {}),
          ...(amazonSettings.lwa_client_id ? { lwa_client_id: amazonSettings.lwa_client_id } : {}),
          ...(amazonSettings.lwa_client_secret ? { lwa_client_secret: amazonSettings.lwa_client_secret } : {}),
          ...(amazonSettings.redirect_uri ? { redirect_uri: amazonSettings.redirect_uri } : {}),
          store_name: amazonSettings.store_name || integration?.name || "",
          region: regionMap[String(amazonSettings.amazon_country || "TR").toUpperCase()] || "tr",
          invoice_settings: {
            ...(integration?.credentials?.invoice_settings || {}),
            vat_rate: parseFloat(amazonSettings.vat_rate || "20"),
            pull_all_orders: amazonSettings.pull_all_orders,
            auto_create_invoice: amazonSettings.auto_create_invoice,
            invoice_statuses: normalizedStatuses,
            invoice_status: normalizedStatuses[0] || "Shipped",
            add_order_note_to_invoice: amazonSettings.add_order_note_to_invoice,
            mark_as_paid: amazonSettings.mark_as_paid,
            payment_account_id: amazonSettings.payment_account_id || "",
            invoice_category_id: amazonSettings.invoice_category_id || "",
            customer_category_id: amazonSettings.customer_category_id || "",
            invoice_tag_id: amazonSettings.invoice_tag_id || "",
            cargo_company: amazonSettings.cargo_company || "",
            tax_exemption_enabled: amazonSettings.tax_exemption_enabled,
            maturity_difference_days: Number(amazonSettings.maturity_difference_days || 0),
            create_waybill_invoice: amazonSettings.create_waybill_invoice,
            international_as_export: amazonSettings.international_as_export,
            auto_create_e_document: amazonSettings.auto_create_e_document,
            e_invoice_type: amazonSettings.e_invoice_type || "basic",
            disable_cargo_for_order: amazonSettings.disable_cargo_for_order,
            auto_email_e_invoice: amazonSettings.auto_email_e_invoice,
            invoice_note: amazonSettings.invoice_note || "",
            daily_order_summary: amazonSettings.daily_order_summary,
            seller_flex_enabled: amazonSettings.seller_flex_enabled,
            selected_warehouse_id: amazonSettings.selected_warehouse_id || "",
            selected_warehouse_name: resolveWarehouseName(amazonSettings.selected_warehouse_id),
          },
        },
      });
      toast.success("Amazon ayarları kaydedildi");
      return true;
    } catch (error) {
      toast.error(error.message || "Amazon ayarları kaydedilemedi");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleAmazonSetupComplete = async () => {
    const saved = await handleAmazonSave();
    if (!saved) return;
    await handleAmazonStartAuth();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Entegrasyon bulunamadı</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/panel/entegrasyonlar")}>
          Geri Dön
        </Button>
      </div>
    );
  }

  const normalizedN11VatRate = normalizeN11VatRate(invoiceSettings.vat_rate);
  const normalizedN11GeneralVatRate = normalizeN11VatRate(n11Settings.vat_rate);

  if (integration.provider === "trendyol") {
    const info = getChannelInfo(integration.provider);
    const isConnected = integration?.status === "success";
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,140,200,0.10),transparent_40%),radial-gradient(circle_at_80%_90%,rgba(16,185,129,0.08),transparent_35%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet
          open={trendyolSheetOpen}
          onOpenChange={(open) => {
            setTrendyolSheetOpen(open);
            if (!open) navigate("/panel/entegrasyonlar");
          }}
        >
          <SheetContent className="fixed right-0 top-0 z-[200] h-screen w-full max-w-full sm:max-w-[680px] p-0 overflow-y-auto">
            <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                  <img src="/integrations/trendyol.svg" alt="Trendyol" className="h-5 w-5 object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">#{integration.integration_no || "-"} Trendyol</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Bağlantı Bekleniyor"}
                </Badge>
              </SheetTitle>
              <SheetDescription>Genel ve özellik ayarlarını buradan yönetin.</SheetDescription>
            </SheetHeader>

            <div className="px-4 sm:px-5 py-4 space-y-4">
              <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="grid h-9 w-full grid-cols-2">
                  <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                  <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-5">
                  <Card>
                    <CardHeader>
                      <CardTitle>API Kimlik Bilgileri</CardTitle>
                      <CardDescription>
                        {isConnected
                          ? "Bağlantı aktif. Güvenlik nedeniyle API bilgileri maskelenmiştir."
                          : "Trendyol satıcı panelindeki entegrasyon bilgilerini girin."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      {isConnected ? (
                        <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-emerald-800 flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4" />
                              API erişimi doğrulandı. Bilgiler güvenlik nedeniyle panelde gizlidir.
                            </div>
                            <Button
                              variant="outline"
                              className="shrink-0"
                              onClick={async () => {
                                setCredentialsGateOpen(true);
                                setVerificationCode("");
                                setVerifiedCredentials(null);
                                await requestCredentialsCode();
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Entegrasyon Bilgileri
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2 md:col-span-2">
                            <Label>Rol Bilgisi</Label>
                            <Input value={trendyolSettings.role_info} onChange={(e) => setTrendyolSettings((s) => ({ ...s, role_info: e.target.value }))} />
                          </div>
                          <div className="space-y-2">
                            <Label>Satıcı ID (Cari ID)</Label>
                            <Input value={trendyolSettings.seller_id} onChange={(e) => setTrendyolSettings((s) => ({ ...s, seller_id: e.target.value }))} />
                          </div>
                          <div className="space-y-2">
                            <Label>Entegrasyon Referans Kodu</Label>
                            <Input value={trendyolSettings.integration_reference_code} onChange={(e) => setTrendyolSettings((s) => ({ ...s, integration_reference_code: e.target.value }))} />
                          </div>
                          <div className="space-y-2">
                            <Label>API Key</Label>
                            <Input value={trendyolSettings.api_key} onChange={(e) => setTrendyolSettings((s) => ({ ...s, api_key: e.target.value }))} />
                          </div>
                          <div className="space-y-2">
                            <Label>API Secret</Label>
                            <Input type="password" value={trendyolSettings.api_secret} onChange={(e) => setTrendyolSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                          </div>
                          <div className="space-y-2">
                            <Label>Token (opsiyonel)</Label>
                            <Input value={trendyolSettings.token} onChange={(e) => setTrendyolSettings((s) => ({ ...s, token: e.target.value }))} />
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Muhasebe ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.payment_account_id}>Ön Muhasebe Kasa</FieldLabel>
                        <Select
                          value={trendyolSettings.payment_account_id || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, payment_account_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutAccounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.selected_warehouse_id}>Paraşüt Deposu</FieldLabel>
                        <Select
                          value={trendyolSettings.selected_warehouse_id || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutWarehouses.map((w) => <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.invoice_tag_id}>Fatura Etiket Seçimi</FieldLabel>
                        <Select
                          value={trendyolSettings.invoice_tag_id || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, invoice_tag_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutTags.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.customer_category_id}>Müşteri Kategori Seçimi</FieldLabel>
                        <Select
                          value={trendyolSettings.customer_category_id || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, customer_category_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Müşteri kategorisi seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {(parasutCategories.contact || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.invoice_category_id}>Fatura Kategori Seçimi</FieldLabel>
                        <Select
                          value={trendyolSettings.invoice_category_id || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, invoice_category_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Fatura kategorisi seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {(parasutCategories.invoice || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.cargo_company}>Kargo Firması</FieldLabel>
                        <Select
                          value={trendyolSettings.cargo_company || "__none"}
                          onValueChange={(value) => setTrendyolSettings((s) => ({ ...s, cargo_company: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Trendyol kargo firması seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {trendyolCargoCompanies.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <FieldLabel help={trendyolHelp.tax_exemption_enabled}>Vergi muafiyetine tabi ürünlerim var</FieldLabel>
                        <div className="flex items-center justify-between rounded-md border p-3">
                          <Switch
                            checked={trendyolSettings.tax_exemption_enabled}
                            onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, tax_exemption_enabled: checked }))}
                          />
                        </div>
                      </div>
                      {trendyolSettings.tax_exemption_enabled ? (
                        <div className="space-y-2 md:col-span-2">
                          <FieldLabel help={trendyolHelp.tax_exemption_code}>Muafiyet Kodu</FieldLabel>
                          <Input
                            list="vat-exemption-options-trendyol"
                            value={trendyolTaxExemptionSearch}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setTrendyolTaxExemptionSearch(nextValue);
                              const parsed = parseVatExemptionSelection(e.target.value);
                              setTrendyolSettings((s) => ({ ...s, tax_exemption_code: parsed.code }));
                            }}
                            placeholder="Kod veya sebep yazarak arayın"
                            className={trendyolSettings.tax_exemption_code ? "border-emerald-300 bg-emerald-50" : ""}
                          />
                          <datalist id="vat-exemption-options-trendyol">
                            {VAT_EXEMPTION_OPTIONS.map((x) => (
                              <option key={`trendyol-${x.code}`} value={formatVatExemptionOption(x)} />
                            ))}
                          </datalist>
                          <FieldLabel help={trendyolHelp.tax_exemption_reason}>Vergi Muafiyet Sebebi</FieldLabel>
                          <Input
                            value={trendyolSettings.tax_exemption_reason || ""}
                            onChange={(e) => setTrendyolSettings((s) => ({ ...s, tax_exemption_reason: e.target.value }))}
                            placeholder="Muafiyet sebebini yazın"
                          />
                        </div>
                      ) : null}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Fatura Notu</Label>
                        <Input value={trendyolSettings.invoice_note} onChange={(e) => setTrendyolSettings((s) => ({ ...s, invoice_note: e.target.value }))} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Otomasyon Kuralları</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Tüm siparişleri panele çek</Label>
                        <Switch checked={trendyolSettings.pull_all_orders} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, pull_all_orders: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Yeni siparişleri otomatik işleme al</Label>
                        <Switch checked={trendyolSettings.auto_pick_orders} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, auto_pick_orders: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Otomatik fatura oluştur</Label>
                        <Switch checked={trendyolSettings.auto_create_invoice} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, auto_create_invoice: checked }))} />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={trendyolHelp.invoice_statuses}>Faturalandırılacak sipariş durumu (birden fazla seçilebilir)</FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {trendyolInvoiceStatuses.map((status) => {
                            const selected = (trendyolSettings.invoice_statuses || []).includes(status);
                            return (
                              <Button
                                key={status}
                                type="button"
                                size="sm"
                                variant={selected ? "default" : "outline"}
                                onClick={() =>
                                  setTrendyolSettings((s) => {
                                    const current = s.invoice_statuses || [];
                                    const next = current.includes(status) ? current.filter((x) => x !== status) : [...current, status];
                                    return { ...s, invoice_statuses: next };
                                  })
                                }
                              >
                                {trendyolStatusLabels[status] || status}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Sipariş notunu faturaya ekle</Label>
                        <Switch checked={trendyolSettings.add_order_note_to_invoice} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, add_order_note_to_invoice: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>e-Fatura / e-Arşiv otomatik oluştur</Label>
                        <Switch checked={trendyolSettings.auto_create_e_document} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, auto_create_e_document: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Faturaları tahsil edildi işaretle</Label>
                        <Switch checked={trendyolSettings.mark_as_paid} onCheckedChange={(checked) => setTrendyolSettings((s) => ({ ...s, mark_as_paid: checked }))} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={handleTrendyolTest} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Bağlantıyı Test Et
              </Button>
              <Button variant="outline" onClick={handleTrendyolSync} disabled={syncing}>
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Siparişleri Çek
              </Button>
              <Button onClick={handleTrendyolSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <Dialog open={credentialsGateOpen} onOpenChange={setCredentialsGateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>E-posta Doğrulaması</DialogTitle>
              <DialogDescription>
                Entegrasyon bilgilerini görmek için e-posta adresinize gelen doğrulama kodunu girin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Doğrulama Kodu</Label>
              <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="6 haneli kod" />
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={requestCredentialsCode} disabled={verificationSending}>
                  {verificationSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Kodu Yeniden Gönder
                </Button>
                <Button onClick={handleVerifyCredentialsCode} disabled={verificationChecking}>
                  {verificationChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Doğrula
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Entegrasyon Bilgileri</DialogTitle>
              <DialogDescription>Doğrulama başarılı. Bilgileri güvenli şekilde görüntülüyorsunuz.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1 md:col-span-2">
                <Label>Rol Bilgisi</Label>
                <Input value={verifiedCredentials?.role_info || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>Satıcı ID (Cari ID)</Label>
                <Input value={verifiedCredentials?.seller_id || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>Entegrasyon Referans Kodu</Label>
                <Input value={verifiedCredentials?.integration_reference_code || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>API Key</Label>
                <Input value={verifiedCredentials?.api_key || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>API Secret</Label>
                <Input value={verifiedCredentials?.api_secret || "-"} readOnly />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Token</Label>
                <div className="max-h-40 overflow-auto rounded-md border px-3 py-2 text-xs font-mono break-all whitespace-pre-wrap leading-5">
                  {verifiedCredentials?.token || "-"}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (integration.provider === "hepsiburada") {
    const isConnected = integration?.status === "success";
    const hasHbCredentials = Boolean(
      String(hepsiburadaSettings?.merchant_id || "").trim() &&
      String(hepsiburadaSettings?.service_key || "").trim()
    );
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,140,200,0.10),transparent_40%),radial-gradient(circle_at_80%_90%,rgba(16,185,129,0.08),transparent_35%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet
          open={trendyolSheetOpen}
          onOpenChange={(open) => {
            setTrendyolSheetOpen(open);
            if (open) fetchParasutData();
            if (!open) navigate("/panel/entegrasyonlar");
          }}
        >
          <SheetContent className="fixed right-0 top-0 z-[200] h-screen w-full max-w-full sm:max-w-[680px] p-0 overflow-y-auto">
            <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                  <img src="/integrations/hepsiburada.svg" alt="Hepsiburada" className="h-5 w-5 object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">#{integration.integration_no || "-"} Hepsiburada</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Bağlantı Bekleniyor"}
                </Badge>
              </SheetTitle>
              <SheetDescription>Genel ve özellik ayarlarını buradan yönetin.</SheetDescription>
            </SheetHeader>
            <div className="px-4 sm:px-5 py-4 space-y-4">
              <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="grid h-9 w-full grid-cols-2">
                  <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                  <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-5">
                  <Card>
                    <CardHeader>
                      <CardTitle>API Kimlik Bilgileri</CardTitle>
                      <CardDescription>
                        {isConnected
                          ? "Bağlantı aktif. Güvenlik nedeniyle API bilgileri maskelenmiştir."
                          : "Hepsiburada satıcı panelindeki entegrasyon bilgilerini girin."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      {isConnected && (
                        <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-emerald-800 flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4" />
                              API erişimi doğrulandı. Kimlik bilgileri varsayılan olarak gizlidir.
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                className="shrink-0"
                                onClick={async () => {
                                  setCredentialsGateOpen(true);
                                  setVerificationCode("");
                                  setVerifiedCredentials(null);
                                  await requestCredentialsCode();
                                }}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Entegrasyon Bilgileri
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {(!isConnected || !hasHbCredentials) && (
                        <>
                          <div className="space-y-2">
                            <Label>Merchant ID</Label>
                            <Input
                              value={hepsiburadaSettings.merchant_id}
                              onChange={(e) => setHepsiburadaSettings((s) => ({ ...s, merchant_id: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <Label>Servis Anahtarı</Label>
                            <Input
                              type="password"
                              value={hepsiburadaSettings.service_key}
                              onChange={(e) => setHepsiburadaSettings((s) => ({ ...s, service_key: e.target.value }))}
                            />
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Muhasebe ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.vat_rate}>KDV Oranı</FieldLabel>
                        <Select
                          value={String(hepsiburadaSettings.vat_rate || "20")}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, vat_rate: value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="KDV oranı seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            {vatRates.map((v) => (
                              <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.prices_include_vat}>Sipariş tutarı KDV dahil</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.prices_include_vat}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, prices_include_vat: checked }))}
                        />
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.payment_account_id}>Ön Muhasebede Kayıtlı Kasa Hesabı</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.payment_account_id || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, payment_account_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutAccounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.selected_warehouse_id}>Paraşüt Deposu</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.selected_warehouse_id || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutWarehouses.map((w) => <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.invoice_tag_id}>Fatura Etiket Seçimi</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.invoice_tag_id || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, invoice_tag_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {parasutTags.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.customer_category_id}>Müşteri Kategori Seçimi</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.customer_category_id || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, customer_category_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Müşteri kategorisi seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {(parasutCategories.contact || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.invoice_category_id}>Fatura Kategori Seçimi</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.invoice_category_id || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, invoice_category_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Fatura kategorisi seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {(parasutCategories.invoice || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.e_invoice_type}>e-Fatura Tipi</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.e_invoice_type}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, e_invoice_type: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="basic">Temel Fatura</SelectItem>
                            <SelectItem value="commercial">Ticari Fatura</SelectItem>
                            <SelectItem value="export">İhracat Faturası</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.cargo_company}>Kargo Firması</FieldLabel>
                        <Select
                          value={hepsiburadaSettings.cargo_company || "__none"}
                          onValueChange={(value) => setHepsiburadaSettings((s) => ({ ...s, cargo_company: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Kargo firması seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {hepsiburadaCargoCompanies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <FieldLabel help={hepsiburadaHelp.tax_exemption_enabled}>Vergi muafiyetine tabi ürünlerim var</FieldLabel>
                        <div className="flex items-center justify-between rounded-md border p-3">
                          <Switch
                            checked={hepsiburadaSettings.tax_exemption_enabled}
                            onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, tax_exemption_enabled: checked }))}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.maturity_difference_days}>Vade Farkı Gün Sayısı</FieldLabel>
                        <Input
                          value={hepsiburadaSettings.maturity_difference_days}
                          onChange={(e) => setHepsiburadaSettings((s) => ({ ...s, maturity_difference_days: e.target.value.replace(/[^0-9]/g, "") }))}
                          placeholder="0"
                        />
                      </div>

                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.create_waybill_invoice}>Faturaları irsaliyeli oluştur</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.create_waybill_invoice}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, create_waybill_invoice: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.international_as_export}>Yurtdışı siparişlerini e-ihracat faturası olarak oluştur</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.international_as_export}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, international_as_export: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.disable_cargo_for_order}>Sipariş için kargo kullanmayacağım</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.disable_cargo_for_order}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, disable_cargo_for_order: checked }))}
                        />
                      </div>

                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.auto_email_e_invoice}>Elektronik faturaları müşterime e-posta ile otomatik gönder</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.auto_email_e_invoice}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, auto_email_e_invoice: checked }))}
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <FieldLabel help={hepsiburadaHelp.invoice_note}>Fatura Notu</FieldLabel>
                        <Textarea
                          value={hepsiburadaSettings.invoice_note}
                          onChange={(e) => setHepsiburadaSettings((s) => ({ ...s, invoice_note: String(e.target.value || "").slice(0, 255) }))}
                          rows={3}
                          placeholder="Fatura notu"
                        />
                        <p className="text-[11px] text-muted-foreground text-right">
                          Kalan karakter sayısı: {255 - String(hepsiburadaSettings.invoice_note || "").length}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Otomasyon Kuralları</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.pull_all_orders}>Tüm siparişlerimi Yengeç paneline çek</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.pull_all_orders}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, pull_all_orders: checked }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={hepsiburadaHelp.invoice_statuses}>Faturalandırılacak Sipariş Durumu</FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {hepsiburadaInvoiceStatuses.map((st) => {
                            const selected = (hepsiburadaSettings.invoice_statuses || []).includes(st.value);
                            return (
                              <Button
                                key={st.value}
                                type="button"
                                size="sm"
                                variant={selected ? "default" : "outline"}
                                onClick={() =>
                                  setHepsiburadaSettings((s) => {
                                    const current = s.invoice_statuses || [];
                                    const next = current.includes(st.value)
                                      ? current.filter((x) => x !== st.value)
                                      : [...current, st.value];
                                    return { ...s, invoice_statuses: next };
                                  })
                                }
                              >
                                {st.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="rounded-md bg-cyan-50 px-3 py-2 text-xs text-cyan-800 font-medium">
                        Faturalarınız Hepsiburada paneline paylaşılıyor
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.auto_create_e_document}>e-Fatura / e-Arşiv dökümanlarını otomatik oluştur</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.auto_create_e_document}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, auto_create_e_document: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.mark_as_paid}>Faturaları tahsil edildi olarak işaretle</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.mark_as_paid}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, mark_as_paid: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.daily_order_summary}>Günlük sipariş özeti gönder</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.daily_order_summary}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, daily_order_summary: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <FieldLabel help={hepsiburadaHelp.add_order_note_to_invoice}>Sipariş notunu faturaya ekle</FieldLabel>
                        <Switch
                          checked={hepsiburadaSettings.add_order_note_to_invoice}
                          onCheckedChange={(checked) => setHepsiburadaSettings((s) => ({ ...s, add_order_note_to_invoice: checked }))}
                        />
                      </div>
                      <div className="rounded-xl border border-border bg-muted/50 p-3 text-xs text-foreground/80 space-y-2">
                        <p className="font-medium text-slate-800">Webhook Aktivasyonu</p>
                        <p>
                          Daha hızlı sipariş entegrasyonu için Hepsiburada’dan webhook aktivasyonu talep edebilirsiniz.
                          Talebinizde mağaza ismi, entegrasyon adı ve callback URL bilgisini iletmeniz önerilir.
                        </p>
                        <button type="button" className="text-primary font-medium hover:underline">Örnek E-Posta</button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={handleHepsiburadaTest} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Bağlantıyı Test Et
              </Button>
              <Button variant="outline" onClick={handleHepsiburadaSync} disabled={syncing}>
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Siparişleri Çek
              </Button>
              <Button onClick={handleHepsiburadaSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <Dialog open={credentialsGateOpen} onOpenChange={setCredentialsGateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>E-posta Doğrulaması</DialogTitle>
              <DialogDescription>
                Entegrasyon bilgilerini görmek için e-posta adresinize gelen doğrulama kodunu girin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Doğrulama Kodu</Label>
              <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="6 haneli kod" />
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={requestCredentialsCode} disabled={verificationSending}>
                  {verificationSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Kodu Yeniden Gönder
                </Button>
                <Button onClick={handleVerifyCredentialsCode} disabled={verificationChecking}>
                  {verificationChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Doğrula
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Entegrasyon Bilgileri</DialogTitle>
              <DialogDescription>Doğrulama başarılı. Bilgileri güvenli şekilde görüntülüyorsunuz.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>Merchant ID</Label>
                <Input value={verifiedCredentials?.merchant_id || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>Servis Anahtarı</Label>
                <Input
                  type="password"
                  value={verifiedCredentials?.service_key || verifiedCredentials?.password || "-"}
                  readOnly
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (integration.provider === "amazon") {
    const isConnected = integration?.status === "success";
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,140,200,0.10),transparent_40%),radial-gradient(circle_at_80%_90%,rgba(16,185,129,0.08),transparent_35%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet
          open={trendyolSheetOpen}
          onOpenChange={(open) => {
            setTrendyolSheetOpen(open);
            if (!open) navigate("/panel/entegrasyonlar");
          }}
        >
          <SheetContent className="w-full max-w-full sm:max-w-[600px] p-0 overflow-y-auto">
            <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                  <img src="/integrations/amazon.svg" alt="Amazon" className="h-5 w-5 object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">#{integration.integration_no || "-"} Amazon</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Bağlantı Bekleniyor"}
                </Badge>
              </SheetTitle>
              <SheetDescription>Genel ve özellik ayarlarını buradan yönetin.</SheetDescription>
            </SheetHeader>

            <div className="px-4 sm:px-5 py-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Entegrasyon Ayarları</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <FieldLabel help={amazonHelp.store_name}>Mağaza İsmi</FieldLabel>
                    <Input
                      value={amazonSettings.store_name}
                      onChange={(e) => setAmazonSettings((s) => ({ ...s, store_name: e.target.value }))}
                      placeholder="Amazon"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>KDV Oranı</Label>
                    <Select
                      value={String(amazonSettings.vat_rate || "20")}
                      onValueChange={(value) => setAmazonSettings((s) => ({ ...s, vat_rate: value }))}
                    >
                      <SelectTrigger><SelectValue placeholder="KDV oranı seçin" /></SelectTrigger>
                      <SelectContent>
                        {vatRates.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <Label>Faturaları irsaliyeli oluştur</Label>
                    <Switch
                      checked={amazonSettings.create_waybill_invoice}
                      onCheckedChange={(checked) => setAmazonSettings((s) => ({ ...s, create_waybill_invoice: checked }))}
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <Label>Faturaları tahsil edildi olarak işaretle</Label>
                    <Switch
                      checked={amazonSettings.mark_as_paid}
                      onCheckedChange={(checked) => setAmazonSettings((s) => ({ ...s, mark_as_paid: checked }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel help={amazonHelp.selected_warehouse_id}>Paraşüt Deposu</FieldLabel>
                    <Select
                      value={amazonSettings.selected_warehouse_id || "__none"}
                      onValueChange={(value) => setAmazonSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}
                    >
                      <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Seçilmedi</SelectItem>
                        {parasutWarehouses.map((w) => <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <Label>Yurtdışı siparişlerini e-ihracat faturası olarak oluştur</Label>
                    <Switch
                      checked={amazonSettings.international_as_export}
                      onCheckedChange={(checked) => setAmazonSettings((s) => ({ ...s, international_as_export: checked }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Fatura Resmileştirme Ayarları</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <Label>e-Fatura / e-Arşiv dokümanlarını otomatik oluştur</Label>
                    <Switch
                      checked={amazonSettings.auto_create_e_document}
                      onCheckedChange={(checked) => setAmazonSettings((s) => ({ ...s, auto_create_e_document: checked }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Bağlantı Ayarları</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label>Amazon Mağaza Ülkesi</Label>
                  <Select
                    value={String(amazonSettings.amazon_country || "TR").toUpperCase()}
                    onValueChange={(value) => setAmazonSettings((s) => ({ ...s, amazon_country: value }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TR">Türkiye</SelectItem>
                      <SelectItem value="EU">Avrupa</SelectItem>
                      <SelectItem value="US">Amerika</SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {isConnected ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Amazon doğrulaması tamamlandı.
                </div>
              ) : null}
            </div>

            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              {isConnected && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      setSyncing(true);
                      const result = await api.post(`/amazon/${id}/sync-orders`, { days_back: 30 });
                      toast.success(`Amazon siparişler güncellendi: ${result.data?.created || 0} yeni, ${result.data?.updated || 0} güncellendi.`);
                    } catch (err) {
                      toast({ title: "Hata", description: err?.response?.data?.detail || "Sipariş çekilemedi.", variant: "destructive" });
                    } finally {
                      setSyncing(false);
                    }
                  }}
                  disabled={syncing}
                >
                  {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Siparişleri Çek
                </Button>
              )}
              {!isConnected && (
                <Button onClick={handleAmazonSetupComplete} disabled={saving || amazonAuthLoading}>
                  {(saving || amazonAuthLoading) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Kurulumu Tamamla
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  if (integration.provider === "pazarama") {
    const isConnected = integration?.status === "success" || integration?.status === "connected";
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(1,54,244,0.08),transparent_40%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet open={trendyolSheetOpen} onOpenChange={(open) => { setTrendyolSheetOpen(open); if (!open) navigate("/panel/entegrasyonlar"); }}>
          <SheetContent className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[520px] p-0 overflow-y-auto">
            <SheetHeader className="px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-white flex items-center justify-center overflow-hidden p-1">
                  <img src="/integrations/pazarama.svg" alt="Pazarama" className="h-full w-full object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">Pazarama Pazaryeri</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Kurulum Gerekli"}
                </Badge>
              </SheetTitle>
              <SheetDescription>Pazarama API bağlantı ayarları.</SheetDescription>
            </SheetHeader>
            <div className="px-5 py-4 space-y-4">
              <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="grid h-9 w-full grid-cols-2">
                  <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                  <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>API Kimlik Bilgileri</CardTitle>
                      <CardDescription>
                        {isConnected ? "Bağlantı aktif. Güvenlik nedeniyle bilgiler maskelenmiştir." : "Pazarama Hesabım → Entegrasyon Bilgileri sayfasından alın."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      {isConnected ? (
                        <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-emerald-800 flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4" />
                              API erişimi doğrulandı. Bilgiler güvenlik nedeniyle panelde gizlidir.
                            </div>
                            <Button variant="outline" className="shrink-0" onClick={async () => { setCredentialsGateOpen(true); setVerificationCode(""); setVerifiedCredentials(null); await requestCredentialsCode(); }}>
                              <Mail className="h-4 w-4 mr-2" /> Entegrasyon Bilgileri
                            </Button>
                          </div>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <Label>API Key</Label>
                        <Input value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>API Secret</Label>
                        <Input type="password" value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Satıcı ID</Label>
                        <Input value={genericSettings.seller_id} onChange={(e) => setGenericSettings((s) => ({ ...s, seller_id: e.target.value }))} placeholder="Entegrasyon Bilgileri sayfasındaki Satıcı ID" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Muhasebe ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>KDV Oranı</Label>
                        <Select
                          value={String(invoiceSettings.vat_rate || "20")}
                          onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, vat_rate: value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="KDV oranı seçin" /></SelectTrigger>
                          <SelectContent>
                            {vatRates.map((r) => <SelectItem key={`pazarama-vat-${r.value}`} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <div className="space-y-2">
                          <Label>Ön Muhasebe Kasa</Label>
                          <Select
                            value={invoiceSettings.payment_account_id || "__none"}
                            onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, payment_account_id: value === "__none" ? "" : value }))}
                          >
                            <SelectTrigger><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">Seçilmedi</SelectItem>
                              {parasutAccounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <Label>{accountingProviderLabel} Deposu</Label>
                        <Select
                          value={invoiceSettings.selected_warehouse_id || "__none"}
                          onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {accountingWarehouses.map((w) => (
                              <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <>
                          <div className="space-y-2">
                            <Label>Fatura Etiketi</Label>
                            <Select
                              value={invoiceSettings.invoice_tag_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_tag_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {parasutTags.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Müşteri Kategorisi</Label>
                            <Select
                              value={invoiceSettings.customer_category_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, customer_category_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.contact || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Fatura Kategorisi</Label>
                            <Select
                              value={invoiceSettings.invoice_category_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_category_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.invoice || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      ) : null}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Fatura Notu</Label>
                        <Input
                          value={invoiceSettings.invoice_note || ""}
                          onChange={(e) => setInvoiceSettings((s) => ({ ...s, invoice_note: e.target.value }))}
                          placeholder="Faturaya eklenecek sabit not"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Sipariş ve Fatura Otomasyonu</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Siparişleri Otomatik Çek</Label></div>
                        <Switch
                          checked={featureSettings.pull_all_orders}
                          onCheckedChange={(checked) => setFeatureSettings((s) => ({ ...s, pull_all_orders: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Otomatik Fatura Oluştur</Label></div>
                        <Switch
                          checked={invoiceSettings.auto_create_invoice}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_invoice: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">e-Belge Otomatik Oluştur</Label></div>
                        <Switch
                          checked={invoiceSettings.auto_create_e_document}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_e_document: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Faturayı Ödendi İşaretle</Label></div>
                        <Switch
                          checked={invoiceSettings.mark_as_paid}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, mark_as_paid: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Yurtdışı Siparişleri e-İhracat Faturası Yap</Label></div>
                        <Switch
                          checked={invoiceSettings.international_as_export}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, international_as_export: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Günlük Sipariş Özeti Gönder</Label></div>
                        <Switch
                          checked={invoiceSettings.daily_order_summary}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, daily_order_summary: checked }))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={handlePazaramaTest} disabled={testing}>
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Bağlantıyı Test Et
              </Button>
              <Button variant="outline" onClick={handlePazaramaSync} disabled={syncing || !isConnected}>
                {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Siparişleri Senkronize Et
              </Button>
              <Button onClick={handlePazaramaSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  if (integration.provider === "idefix") {
    const isConnected = integration?.status === "success" || integration?.status === "connected";
    const selectedIdefixStatuses = Array.isArray(invoiceSettings.invoice_statuses)
      ? invoiceSettings.invoice_statuses
      : String(invoiceSettings.invoice_status || "shipment_in_cargo")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(232,74,12,0.08),transparent_40%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet open={trendyolSheetOpen} onOpenChange={(open) => { setTrendyolSheetOpen(open); if (!open) navigate("/panel/entegrasyonlar"); }}>
          <SheetContent className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[520px] p-0 overflow-y-auto">
            <SheetHeader className="px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-white flex items-center justify-center overflow-hidden p-1">
                  <img src="/integrations/idefix.svg" alt="Idefix" className="h-full w-full object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">Idefix Pazaryeri</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Kurulum Gerekli"}
                </Badge>
              </SheetTitle>
              <SheetDescription>Idefix API bağlantı ayarları.</SheetDescription>
            </SheetHeader>
            <div className="px-5 py-4 space-y-4">
              <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="grid h-9 w-full grid-cols-2">
                  <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                  <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>API Kimlik Bilgileri</CardTitle>
                      <CardDescription>
                        {isConnected ? "Bağlantı aktif. Güvenlik nedeniyle bilgiler maskelenmiştir." : "Idefix Hesabım → Entegrasyon Bilgileri sayfasından alın."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      {isConnected ? (
                        <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-emerald-800 flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4" />
                              API erişimi doğrulandı. Bilgiler güvenlik nedeniyle gizlidir.
                            </div>
                            <Button variant="outline" className="shrink-0" onClick={async () => { setCredentialsGateOpen(true); setVerificationCode(""); setVerifiedCredentials(null); await requestCredentialsCode(); }}>
                              <Mail className="h-4 w-4 mr-2" /> Entegrasyon Bilgileri
                            </Button>
                          </div>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <FieldLabel help={idefixHelp.vendor_id}>Satıcı ID</FieldLabel>
                        <Input value={genericSettings.vendor_id} onChange={(e) => setGenericSettings((s) => ({ ...s, vendor_id: e.target.value }))} placeholder="Idefix Hesabım → Entegrasyon Bilgileri" />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={idefixHelp.api_key}>Token</FieldLabel>
                        <Input type="password" value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <FieldLabel help={idefixHelp.api_secret}>Secret</FieldLabel>
                        <Input type="password" value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Muhasebe ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>KDV Oranı</Label>
                        <Select value={String(invoiceSettings.vat_rate || "20")} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, vat_rate: value }))}>
                          <SelectTrigger><SelectValue placeholder="KDV oranı seçin" /></SelectTrigger>
                          <SelectContent>
                            {vatRates.map((r) => <SelectItem key={`idefix-vat-${r.value}`} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <div className="space-y-2">
                          <Label>Ön Muhasebe Kasa</Label>
                          <Select value={invoiceSettings.payment_account_id || "__none"} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, payment_account_id: value === "__none" ? "" : value }))}>
                            <SelectTrigger><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">Seçilmedi</SelectItem>
                              {parasutAccounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <FieldLabel help={idefixHelp.selected_warehouse_id}>{accountingProviderLabel} Deposu</FieldLabel>
                        <Select value={invoiceSettings.selected_warehouse_id || "__none"} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}>
                          <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {accountingWarehouses.map((w) => <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <>
                          <div className="space-y-2">
                            <Label>Fatura Etiketi</Label>
                            <Select value={invoiceSettings.invoice_tag_id || "__none"} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_tag_id: value === "__none" ? "" : value }))}>
                              <SelectTrigger><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {parasutTags.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Müşteri Kategorisi</Label>
                            <Select value={invoiceSettings.customer_category_id || "__none"} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, customer_category_id: value === "__none" ? "" : value }))}>
                              <SelectTrigger><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.contact || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <FieldLabel help={idefixHelp.invoice_category_id}>Fatura Kategorisi</FieldLabel>
                            <Select value={invoiceSettings.invoice_category_id || "__none"} onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_category_id: value === "__none" ? "" : value }))}>
                              <SelectTrigger><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.invoice || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      ) : null}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Fatura Notu</Label>
                        <Input value={invoiceSettings.invoice_note || ""} onChange={(e) => setInvoiceSettings((s) => ({ ...s, invoice_note: e.target.value }))} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Sipariş ve Fatura Otomasyonu</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Siparişleri Otomatik Çek</Label></div>
                        <Switch checked={featureSettings.pull_all_orders} onCheckedChange={(checked) => setFeatureSettings((s) => ({ ...s, pull_all_orders: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Siparişleri Otomatik Kabul Et</Label></div>
                        <Switch checked={featureSettings.auto_pick_orders} onCheckedChange={(checked) => setFeatureSettings((s) => ({ ...s, auto_pick_orders: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Otomatik Fatura Oluştur</Label></div>
                        <Switch checked={invoiceSettings.auto_create_invoice} onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_invoice: checked }))} />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help={idefixHelp.invoice_statuses}>Faturalandırılacak shipment durumu</FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {idefixInvoiceStatuses.map((status) => {
                            const selected = selectedIdefixStatuses.includes(status.value);
                            return (
                              <Button
                                key={status.value}
                                type="button"
                                size="sm"
                                variant={selected ? "default" : "outline"}
                                onClick={() =>
                                  setInvoiceSettings((s) => {
                                    const current = Array.isArray(s.invoice_statuses) ? s.invoice_statuses : [];
                                    const next = current.includes(status.value)
                                      ? current.filter((x) => x !== status.value)
                                      : [...current, status.value];
                                    return { ...s, invoice_statuses: next, invoice_status: next[0] || "shipment_in_cargo" };
                                  })
                                }
                              >
                                {status.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">e-Belge Otomatik Oluştur</Label></div>
                        <Switch checked={invoiceSettings.auto_create_e_document} onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_e_document: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Faturayı Ödendi İşaretle</Label></div>
                        <Switch checked={invoiceSettings.mark_as_paid} onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, mark_as_paid: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Yurtdışı Siparişleri e-İhracat Faturası Yap</Label></div>
                        <Switch checked={invoiceSettings.international_as_export} onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, international_as_export: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div><Label className="text-sm font-medium">Günlük Sipariş Özeti Gönder</Label></div>
                        <Switch checked={invoiceSettings.daily_order_summary} onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, daily_order_summary: checked }))} />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={handleIdefixTest} disabled={testing}>
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Bağlantıyı Test Et
              </Button>
              <Button variant="outline" onClick={handleIdefixSync} disabled={syncing || !isConnected}>
                {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Siparişleri Senkronize Et
              </Button>
              <Button onClick={handleIdefixSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  if (integration.provider === "n11") {
    const isConnected = integration?.status === "success";
    const selectedN11Statuses = Array.isArray(featureSettings.invoice_statuses)
      ? featureSettings.invoice_statuses
      : String(featureSettings.invoice_status || invoiceSettings.invoice_status || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
    return (
      <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,140,200,0.10),transparent_40%),radial-gradient(circle_at_80%_90%,rgba(16,185,129,0.08),transparent_35%)]" />
        <div className="relative p-6">
          <h1 className="app-page-title">Entegrasyon Ayarları</h1>
          <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
        </div>
        <Sheet
          open={trendyolSheetOpen}
          onOpenChange={(open) => {
            setTrendyolSheetOpen(open);
            if (!open) navigate("/panel/entegrasyonlar");
          }}
        >
          <SheetContent className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[560px] p-0 overflow-y-auto">
            <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
              <SheetTitle className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                  <img src="/integrations/n11.png" alt="N11" className="h-5 w-5 object-contain" />
                </div>
                <div className="text-left">
                  <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                  <div className="text-xs text-muted-foreground">#{integration.integration_no || "-"} N11</div>
                </div>
                <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {isConnected ? "Bağlı" : "Bağlantı Bekleniyor"}
                </Badge>
              </SheetTitle>
            <SheetDescription>N11 API bağlantı ayarlarını buradan yönetin.</SheetDescription>
          </SheetHeader>

            <div className="px-4 sm:px-5 py-4 space-y-4" data-testid="integration-settings-page">
              <Tabs defaultValue="general" className="space-y-4">
                <TabsList className="grid h-9 w-full grid-cols-2">
                  <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                  <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>API Kimlik Bilgileri</CardTitle>
                      <CardDescription>
                        {isConnected
                          ? "Bağlantı aktif. Güvenlik nedeniyle API bilgileri maskelenmiştir."
                          : "N11 entegrasyonu için API Anahtarı ve Anahtar Şifresi girin."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      {isConnected ? (
                        <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-emerald-800 flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4" />
                              API erişimi doğrulandı. Bilgiler güvenlik nedeniyle panelde gizlidir.
                            </div>
                            <Button
                              variant="outline"
                              className="shrink-0"
                              onClick={async () => {
                                setCredentialsGateOpen(true);
                                setVerificationCode("");
                                setVerifiedCredentials(null);
                                await requestCredentialsCode();
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Entegrasyon Bilgileri
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>API Anahtarı</Label>
                            <Input
                              value={genericSettings.api_key}
                              onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Anahtar Şifresi</Label>
                            <Input
                              type="password"
                              value={genericSettings.api_secret}
                              onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))}
                            />
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Muhasebe ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <FieldLabel>KDV Oranı</FieldLabel>
                        <Select
                          value={normalizedN11VatRate}
                          onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, vat_rate: value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="KDV oranı seçin" /></SelectTrigger>
                          <SelectContent className="z-[600]">
                            {n11VatRates.map((r) => <SelectItem key={`n11-vat-${r.value}`} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <div className="space-y-2">
                          <FieldLabel>Ön Muhasebe Kasa</FieldLabel>
                          <Select
                            value={invoiceSettings.payment_account_id || "__none"}
                            onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, payment_account_id: value === "__none" ? "" : value }))}
                          >
                            <SelectTrigger><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">Seçilmedi</SelectItem>
                              {parasutAccounts.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                      <div className="space-y-2">
                        <FieldLabel>{accountingProviderLabel} Deposu</FieldLabel>
                        <Select
                          value={invoiceSettings.selected_warehouse_id || "__none"}
                          onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, selected_warehouse_id: value === "__none" ? "" : value }))}
                        >
                          <SelectTrigger><SelectValue placeholder="Depo seçin" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Seçilmedi</SelectItem>
                            {accountingWarehouses.map((w) => (
                              <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {accountingProvider !== "bizimhesap" ? (
                        <>
                          <div className="space-y-2">
                            <FieldLabel>Fatura Etiket Seçimi</FieldLabel>
                            <Select
                              value={invoiceSettings.invoice_tag_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_tag_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Etiket seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {parasutTags.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <FieldLabel>Müşteri Kategori Seçimi</FieldLabel>
                            <Select
                              value={invoiceSettings.customer_category_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, customer_category_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Müşteri kategorisi seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.contact || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <FieldLabel>Fatura Kategori Seçimi</FieldLabel>
                            <Select
                              value={invoiceSettings.invoice_category_id || "__none"}
                              onValueChange={(value) => setInvoiceSettings((s) => ({ ...s, invoice_category_id: value === "__none" ? "" : value }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Fatura kategorisi seçin" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none">Seçilmedi</SelectItem>
                                {(parasutCategories.invoice || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      ) : null}
                      <div className="space-y-2 md:col-span-2">
                        <FieldLabel>Vergi muafiyetine tabi ürünlerim var</FieldLabel>
                        <div className="flex items-center justify-between rounded-md border p-3">
                          <Switch
                            checked={invoiceSettings.tax_exemption_enabled}
                            onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, tax_exemption_enabled: checked }))}
                          />
                        </div>
                      </div>
                      {invoiceSettings.tax_exemption_enabled ? (
                        <div className="space-y-2 md:col-span-2">
                          <FieldLabel>Muafiyet Kodu</FieldLabel>
                          <Input
                            list="vat-exemption-options-n11"
                            value={n11TaxExemptionSearch}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setN11TaxExemptionSearch(nextValue);
                              const parsed = parseVatExemptionSelection(e.target.value);
                              setInvoiceSettings((s) => ({
                                ...s,
                                tax_exemption_code: parsed.code,
                              }));
                            }}
                            placeholder="Kod veya sebep yazarak arayın"
                            className={invoiceSettings.tax_exemption_code ? "border-emerald-300 bg-emerald-50" : ""}
                          />
                          <datalist id="vat-exemption-options-n11">
                            {VAT_EXEMPTION_OPTIONS.map((x) => (
                              <option key={`n11-${x.code}`} value={formatVatExemptionOption(x)} />
                            ))}
                          </datalist>
                          <FieldLabel>Vergi Muafiyet Sebebi</FieldLabel>
                          <Input
                            value={invoiceSettings.tax_exemption_reason || ""}
                            onChange={(e) => setInvoiceSettings((s) => ({ ...s, tax_exemption_reason: e.target.value }))}
                            placeholder="Muafiyet sebebini yazın"
                          />
                        </div>
                      ) : null}
                      <div className="space-y-2 md:col-span-2">
                        <Label>Fatura Notu</Label>
                        <Input
                          value={invoiceSettings.invoice_note}
                          onChange={(e) => setInvoiceSettings((s) => ({ ...s, invoice_note: e.target.value }))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="features" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Sipariş ve Fatura</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">Siparişleri Otomatik Çek</Label>
                        </div>
                        <Switch
                          checked={featureSettings.pull_all_orders}
                          onCheckedChange={(checked) => setFeatureSettings((s) => ({ ...s, pull_all_orders: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">Otomatik Fatura Oluştur</Label>
                        </div>
                        <Switch
                          checked={invoiceSettings.auto_create_invoice}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_invoice: checked }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel>Faturalandırılacak sipariş durumu (birden fazla seçilebilir)</FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {n11InvoiceStatuses.map((status) => {
                            const selected = selectedN11Statuses.includes(status.value);
                            return (
                              <Button
                                key={status.value}
                                type="button"
                                size="sm"
                                variant={selected ? "default" : "outline"}
                                onClick={() =>
                                  setFeatureSettings((s) => {
                                    const current = selectedN11Statuses;
                                    const next = current.includes(status.value)
                                      ? current.filter((x) => x !== status.value)
                                      : [...current, status.value];
                                    return { ...s, invoice_statuses: next, invoice_status: next[0] || "Delivered" };
                                  })
                                }
                              >
                                {status.label}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">e-Belge Otomatik Oluştur</Label>
                        </div>
                        <Switch
                          checked={invoiceSettings.auto_create_e_document}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, auto_create_e_document: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">Faturayı Ödendi İşaretle</Label>
                        </div>
                        <Switch
                          checked={invoiceSettings.mark_as_paid}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, mark_as_paid: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">Yurtdışı siparişlerini e-ihracat faturası olarak oluştur</Label>
                        </div>
                        <Switch
                          checked={invoiceSettings.international_as_export}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, international_as_export: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                          <Label className="text-sm font-medium">Günlük sipariş özeti gönder</Label>
                        </div>
                        <Switch
                          checked={invoiceSettings.daily_order_summary}
                          onCheckedChange={(checked) => setInvoiceSettings((s) => ({ ...s, daily_order_summary: checked }))}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={handleN11Test} disabled={testing}>
                {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Bağlantıyı Test Et
              </Button>
              <Button variant="outline" onClick={() => navigate("/panel/entegrasyonlar")}>
                Vazgeç
              </Button>
              <Button onClick={handleGenericSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        <Dialog open={credentialsGateOpen} onOpenChange={setCredentialsGateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>E-posta Doğrulaması</DialogTitle>
              <DialogDescription>
                Entegrasyon bilgilerini görmek için e-posta adresinize gelen doğrulama kodunu girin.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Doğrulama Kodu</Label>
              <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="6 haneli kod" />
              <div className="flex justify-between gap-2">
                <Button variant="outline" onClick={requestCredentialsCode} disabled={verificationSending}>
                  {verificationSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Kodu Yeniden Gönder
                </Button>
                <Button onClick={handleVerifyCredentialsCode} disabled={verificationChecking}>
                  {verificationChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Doğrula
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Entegrasyon Bilgileri</DialogTitle>
              <DialogDescription>Doğrulama başarılı. Bilgileri güvenli şekilde görüntülüyorsunuz.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>API Key</Label>
                <Input value={verifiedCredentials?.app_key || verifiedCredentials?.api_key || "-"} readOnly />
              </div>
              <div className="space-y-1">
                <Label>API Secret</Label>
                <Input value={verifiedCredentials?.app_secret || verifiedCredentials?.api_secret || "-"} readOnly />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (integration.provider !== "woocommerce") {
    const info = getChannelInfo(integration.provider);
    return (
      <div className="page-shell space-y-6">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/panel/entegrasyonlar")} className="h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted/50 inline-flex items-center justify-center transition-colors shrink-0">
              <ArrowLeft className="h-4 w-4 text-muted-foreground" />
            </button>
            <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center font-semibold shrink-0", info.color)}>
              <Globe className="h-4 w-4" />
            </div>
            <div className="app-page-header">
              <h1 className="app-page-title">{integration.name}</h1>
              <p className="app-page-subtitle">{info.label} Entegrasyon Ayarları</p>
            </div>
          </div>
          <div className="page-header-actions">
            <Badge className={integration.status === "success" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
              {integration.status === "success" ? "Bağlı" : "Beklemede"}
            </Badge>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Genel Baglanti Ayarlari</CardTitle>
            <CardDescription>Farkli kargo/pazaryeri/e-ticaret saglayicilari icin temel kimlik bilgileri.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            {integration.provider === "n11" ? (
              <>
                <div className="space-y-2">
                  <Label>API Anahtarı</Label>
                  <Input value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Anahtar Şifresi</Label>
                  <Input type="password" value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                </div>
              </>
            ) : integration.provider === "pazarama" ? (
              <>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>API Secret</Label>
                  <Input type="password" value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Satıcı ID</Label>
                  <Input value={genericSettings.seller_id} onChange={(e) => setGenericSettings((s) => ({ ...s, seller_id: e.target.value }))} placeholder="Pazarama entegrasyon bilgileri sayfasından" />
                </div>
              </>
            ) : integration.provider === "idefix" ? (
              <>
                <div className="space-y-2">
                  <Label>Satıcı ID</Label>
                  <Input value={genericSettings.vendor_id} onChange={(e) => setGenericSettings((s) => ({ ...s, vendor_id: e.target.value }))} placeholder="Idefix Hesabım → Entegrasyon Bilgileri" />
                </div>
                <div className="space-y-2">
                  <Label>Token</Label>
                  <Input type="password" value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Secret</Label>
                  <Input type="password" value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>URL / Endpoint</Label>
                  <Input value={genericSettings.url} onChange={(e) => setGenericSettings((s) => ({ ...s, url: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input value={genericSettings.api_key} onChange={(e) => setGenericSettings((s) => ({ ...s, api_key: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>API Secret</Label>
                  <Input value={genericSettings.api_secret} onChange={(e) => setGenericSettings((s) => ({ ...s, api_secret: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input value={genericSettings.username} onChange={(e) => setGenericSettings((s) => ({ ...s, username: e.target.value }))} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Password</Label>
                  <Input type="password" value={genericSettings.password} onChange={(e) => setGenericSettings((s) => ({ ...s, password: e.target.value }))} />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          {integration.provider === "n11" ? (
            <Button variant="outline" onClick={handleN11Test} disabled={testing}>
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
              Bağlantıyı Test Et
            </Button>
          ) : null}
          <Button onClick={handleGenericSave} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Settings2 className="mr-2 h-4 w-4" />}
            Kaydet
          </Button>
        </div>
      </div>
    );
  }

  const isConnected = integration?.status === "success";
  const hasWooCredentials = Boolean(
    String(apiSettings.store_url || "").trim() &&
    String(apiSettings.consumer_key || "").trim() &&
    String(apiSettings.consumer_secret || "").trim()
  );
  const showCredentialMask = Boolean(isConnected && hasWooCredentials);

  return (
    <div className="relative h-[calc(100vh-120px)] overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(37,140,200,0.10),transparent_40%),radial-gradient(circle_at_80%_90%,rgba(16,185,129,0.08),transparent_35%)]" />
      <div className="relative p-4 sm:p-6">
        <h1 className="app-page-title">Entegrasyon Ayarları</h1>
        <p className="text-sm text-muted-foreground">Ayarlar sağ panelde açılır.</p>
      </div>
      <Sheet
        open={trendyolSheetOpen}
        onOpenChange={(open) => {
          setTrendyolSheetOpen(open);
          if (!open) navigate("/panel/entegrasyonlar");
        }}
      >
        <SheetContent className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[680px] p-0 overflow-y-auto">
          <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
            <SheetTitle className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                <img src="/integrations/woocommerce.svg" alt="WooCommerce" className="h-5 w-5 object-contain" />
              </div>
              <div className="text-left">
                <div className="text-lg font-semibold leading-tight">{integration.name}</div>
                <div className="text-xs text-muted-foreground">#{integration.integration_no || "-"} WooCommerce</div>
              </div>
              <Badge className={isConnected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {isConnected ? "Bağlı" : "Bağlantı Bekleniyor"}
              </Badge>
            </SheetTitle>
            <SheetDescription>Genel ve özellik ayarlarını buradan yönetin.</SheetDescription>
          </SheetHeader>

          <div className="px-4 sm:px-5 py-4 space-y-4" data-testid="integration-settings-page">
            <Tabs defaultValue="general" className="space-y-4">
              <TabsList className="grid h-9 w-full grid-cols-2">
                <TabsTrigger value="general">Genel Ayarlar</TabsTrigger>
                <TabsTrigger value="features">Özellik Ayarları</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-5">
                <Card>
                <CardHeader>
                  <CardTitle>API Kimlik Bilgileri</CardTitle>
                  <CardDescription>
                      {showCredentialMask
                        ? "Bağlantı aktif. Güvenlik nedeniyle API bilgileri maskelenmiştir. Güncellemek için düzenleme modunu açın."
                        : "WooCommerce REST API bilgilerinizi girin."}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    {showCredentialMask ? (
                      <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-sm text-emerald-800 flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4" />
                            API erişimi doğrulandı. Bilgiler güvenlik nedeniyle panelde gizlidir.
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              className="shrink-0"
                              onClick={async () => {
                                setCredentialsGateOpen(true);
                                setVerificationCode("");
                                setVerifiedCredentials(null);
                                await requestCredentialsCode();
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              API Bilgileri
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-2 md:col-span-2">
                          <FieldLabel help="WooCommerce mağazanızın tam URL adresi (https:// ile).">Mağaza URL</FieldLabel>
                          <Input
                            placeholder="https://magaza.com"
                            value={apiSettings.store_url}
                            onChange={(e) => setApiSettings({ ...apiSettings, store_url: e.target.value })}
                            data-testid="store-url-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel help="WooCommerce > Gelişmiş > REST API ekranından oluşturulan anahtar.">Consumer Key</FieldLabel>
                          <Input
                            placeholder="ck_xxxxxxxxxxxxxxxx"
                            value={apiSettings.consumer_key}
                            onChange={(e) => setApiSettings({ ...apiSettings, consumer_key: e.target.value })}
                            data-testid="consumer-key-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel help="Consumer Key ile eşleşen gizli anahtar.">Consumer Secret</FieldLabel>
                          <Input
                            type="password"
                            placeholder="cs_xxxxxxxxxxxxxxxx"
                            value={apiSettings.consumer_secret}
                            onChange={(e) => setApiSettings({ ...apiSettings, consumer_secret: e.target.value })}
                            data-testid="consumer-secret-input"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <FieldLabel help="WooCommerce webhook doğrulaması için opsiyonel gizli anahtar.">Webhook Secret</FieldLabel>
                          <Input
                            placeholder="Webhook doğrulama anahtarı"
                            value={apiSettings.webhook_secret}
                            onChange={(e) => setApiSettings({ ...apiSettings, webhook_secret: e.target.value })}
                            data-testid="webhook-secret-input"
                          />
                        </div>
                        <div className="md:col-span-2 text-xs text-muted-foreground">
                          <a
                            href="https://woocommerce.github.io/woocommerce-rest-api-docs/#authentication"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center"
                          >
                            API anahtarı nasıl alınır?
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Muhasebe ve Fatura</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <FieldLabel help="Paraşüt'e kesilecek faturaların varsayılan kategorisi.">Fatura Kategorisi</FieldLabel>
                      <Select
                        value={invoiceSettings.invoice_category_id || "none"}
                        onValueChange={(value) => setInvoiceSettings({ ...invoiceSettings, invoice_category_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="invoice-category-select">
                          <SelectValue placeholder="Kategori seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kategorisiz</SelectItem>
                          {parasutCategories.invoice.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <FieldLabel help="Paraşüt'e aktarılacak müşteri kartının kategorisi.">Müşteri Kategorisi</FieldLabel>
                      <Select
                        value={invoiceSettings.customer_category_id || "none"}
                        onValueChange={(value) => setInvoiceSettings({ ...invoiceSettings, customer_category_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="customer-category-select">
                          <SelectValue placeholder="Kategori seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kategorisiz</SelectItem>
                          {parasutCategories.contact.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <FieldLabel help="Fatura satırlarında kullanılacak varsayılan KDV oranı.">Varsayılan KDV Oranı</FieldLabel>
                      <Select
                        value={invoiceSettings.vat_rate}
                        onValueChange={(value) => setInvoiceSettings({ ...invoiceSettings, vat_rate: value })}
                      >
                        <SelectTrigger data-testid="vat-rate-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {vatRates.map((rate) => (
                            <SelectItem key={rate.value} value={rate.value}>{rate.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <FieldLabel help="Tahsilat otomasyonu için kullanılacak kasa/banka hesabı.">Tahsilat Hesabı</FieldLabel>
                      <Select
                        value={invoiceSettings.payment_account_id || "none"}
                        onValueChange={(value) => setInvoiceSettings({ ...invoiceSettings, payment_account_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="payment-account-select">
                          <SelectValue placeholder="Hesap seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seçilmedi</SelectItem>
                          {parasutAccounts.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <FieldLabel help="Bu entegrasyonun ürün eşleştirme ekranında kullanılacak Paraşüt deposu.">Paraşüt Deposu</FieldLabel>
                      <Select
                        value={invoiceSettings.selected_warehouse_id || "none"}
                        onValueChange={(value) => setInvoiceSettings({ ...invoiceSettings, selected_warehouse_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Depo seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seçilmedi</SelectItem>
                          {parasutWarehouses.map((wh) => (
                            <SelectItem key={wh.id} value={String(wh.id)}>{wh.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label>Fatura Notu</Label>
                      <Input
                        placeholder="Faturaya eklenecek sabit not"
                        value={invoiceSettings.invoice_note}
                        onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_note: e.target.value })}
                        data-testid="invoice-note-input"
                      />
                    </div>

                    <div className="md:col-span-2 space-y-3 pt-1">
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Otomatik fatura oluştur</Label>
                        <Switch
                          checked={invoiceSettings.auto_create_invoice}
                          onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, auto_create_invoice: checked })}
                          data-testid="auto-invoice-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>e-Arşiv / e-Fatura otomatik oluştur</Label>
                        <Switch
                          checked={invoiceSettings.auto_create_e_document}
                          onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, auto_create_e_document: checked })}
                          data-testid="auto-e-document-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <Label>Faturayı tahsil edildi işaretle</Label>
                        <Switch
                          checked={invoiceSettings.mark_as_paid}
                          onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, mark_as_paid: checked })}
                          data-testid="auto-payment-switch"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="features" className="space-y-5">
                <Card>
                  <CardHeader>
                    <CardTitle>Otomasyon Kuralları</CardTitle>
                    <CardDescription>Sipariş senkronizasyonu ve fatura tetik koşulları.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <Label>Tüm siparişleri panele çek</Label>
                      <Switch
                        checked={featureSettings.pull_all_orders}
                        onCheckedChange={(checked) => setFeatureSettings({ ...featureSettings, pull_all_orders: checked })}
                        data-testid="pull-orders-switch"
                      />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel help="Seçilen WooCommerce durumlarına gelen siparişler faturalandırma otomasyonuna girer.">Faturalandırılacak sipariş durumu (birden fazla seçilebilir)</FieldLabel>
                      <div className="flex flex-wrap gap-2" data-testid="invoice-status-multiselect">
                        {wcStatuses.map((status) => {
                          const selected = (invoiceSettings.invoice_statuses || []).includes(status.value);
                          return (
                            <Button
                              key={status.value}
                              type="button"
                              size="sm"
                              variant={selected ? "default" : "outline"}
                              onClick={() =>
                                setInvoiceSettings((s) => {
                                  const current = Array.isArray(s.invoice_statuses) ? s.invoice_statuses : [];
                                  const next = current.includes(status.value)
                                    ? current.filter((x) => x !== status.value)
                                    : [...current, status.value];
                                  return {
                                    ...s,
                                    invoice_statuses: next,
                                    invoice_status: next[0] || "completed",
                                  };
                                })
                              }
                            >
                              {status.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <Label>Sipariş notunu faturaya ekle</Label>
                      <Switch
                        checked={invoiceSettings.add_order_note_to_invoice}
                        onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, add_order_note_to_invoice: checked })}
                        data-testid="add-note-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <Label>Sadece ürün faturalandır (kargo hariç)</Label>
                      <Switch
                        checked={invoiceSettings.products_only_from_gross_total}
                        onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, products_only_from_gross_total: checked })}
                        data-testid="products-only-invoice-switch"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>WooCommerce Alan Eşleştirme</CardTitle>
                    <CardDescription>
                      WooCommerce özel alanlarını Paraşüt müşteri/fatura alanlarıyla eşleştirin.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      {loadingWooMeta
                        ? "WooCommerce alanları taranıyor..."
                        : wooMetaKeys.length > 0
                          ? `${wooMetaKeys.length} alan bulundu. Listeden seçebilir veya manuel yazabilirsiniz.`
                          : "Alan bulunamadı. Yine de manuel alan adı yazabilirsiniz."}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <FieldLabel help="Müşteri vergi dairesi bilgisinin bulunduğu meta alanı.">Vergi Dairesi Alanı</FieldLabel>
                        <Input
                          placeholder="_billing_tax_office"
                          list="woo-meta-keys-list"
                          value={featureSettings.tax_office_field}
                          onChange={(e) => setFeatureSettings({ ...featureSettings, tax_office_field: e.target.value })}
                          data-testid="tax-office-field-input"
                        />
                        {wooMetaSamples[featureSettings.tax_office_field] && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            Örnek: {wooMetaSamples[featureSettings.tax_office_field]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help="Müşteri vergi numarası bilgisinin bulunduğu meta alanı.">Vergi Numarası Alanı</FieldLabel>
                        <Input
                          placeholder="_billing_tax_number"
                          list="woo-meta-keys-list"
                          value={featureSettings.tax_number_field}
                          onChange={(e) => setFeatureSettings({ ...featureSettings, tax_number_field: e.target.value })}
                          data-testid="tax-number-field-input"
                        />
                        {wooMetaSamples[featureSettings.tax_number_field] && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            Örnek: {wooMetaSamples[featureSettings.tax_number_field]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help="Bireysel müşterilerde TCKN bilgisinin bulunduğu meta alanı.">TC Kimlik No Alanı</FieldLabel>
                        <Input
                          placeholder="_billing_tc_number"
                          list="woo-meta-keys-list"
                          value={featureSettings.tc_number_field}
                          onChange={(e) => setFeatureSettings({ ...featureSettings, tc_number_field: e.target.value })}
                          data-testid="tc-number-field-input"
                        />
                        {wooMetaSamples[featureSettings.tc_number_field] && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            Örnek: {wooMetaSamples[featureSettings.tc_number_field]}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <FieldLabel help="Sipariş notunu fatura notuna taşımak için kullanılacak meta alanı.">Fatura Notu Alanı</FieldLabel>
                        <Input
                          placeholder="_billing_invoice_note"
                          list="woo-meta-keys-list"
                          value={featureSettings.invoice_note_field}
                          onChange={(e) => setFeatureSettings({ ...featureSettings, invoice_note_field: e.target.value })}
                          data-testid="invoice-note-field-input"
                        />
                        {wooMetaSamples[featureSettings.invoice_note_field] && (
                          <p className="text-[11px] text-muted-foreground truncate">
                            Örnek: {wooMetaSamples[featureSettings.invoice_note_field]}
                          </p>
                        )}
                      </div>
                    </div>
                    <datalist id="woo-meta-keys-list">
                      {wooMetaKeys.map((k) => (
                        <option key={k} value={k} />
                      ))}
                    </datalist>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid="test-connection-btn"
            >
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Bağlantıyı Test Et
            </Button>
            <Button
              variant="outline"
              onClick={handleSyncOrders}
              disabled={syncing || !apiSettings.consumer_key}
              data-testid="sync-orders-btn"
            >
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Siparişleri Çek
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings2 className="h-4 w-4 mr-2" />}
              Kaydet
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={credentialsGateOpen} onOpenChange={setCredentialsGateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>E-posta Doğrulaması</DialogTitle>
            <DialogDescription>
              Entegrasyon bilgilerini görmek için e-posta adresinize gelen doğrulama kodunu girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Doğrulama Kodu</Label>
            <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="6 haneli kod" />
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={requestCredentialsCode} disabled={verificationSending}>
                {verificationSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Kodu Yeniden Gönder
              </Button>
              <Button onClick={handleVerifyCredentialsCode} disabled={verificationChecking}>
                {verificationChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Doğrula
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Entegrasyon Bilgileri</DialogTitle>
            <DialogDescription>Doğrulama başarılı. Bilgileri güvenli şekilde görüntülüyorsunuz.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label>Mağaza URL</Label>
              <Input value={verifiedCredentials?.store_url || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Consumer Key</Label>
              <Input value={verifiedCredentials?.consumer_key || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Consumer Secret</Label>
              <Input value={verifiedCredentials?.consumer_secret || "-"} readOnly />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Webhook Secret</Label>
              <Input value={verifiedCredentials?.webhook_secret || "-"} readOnly />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
